/**
 * ============================================================================
 *  MIRRORED FILE — keep byte-identical with the backend copy at:
 *      api/src/modules/reports/docx/chart/chart-config.ts
 *
 *  This module is the single source of truth for turning a normalized
 *  `ChartSpec` into an Apache ECharts option object. The same option is
 *  rendered server-side (ECharts SSR → SVG → DOCX, with a PNG fallback) and
 *  client-side (ECharts SVG renderer → preview), which is what guarantees that
 *  the /report preview matches the exported .docx ("WYSIWYG").
 *
 *  Any change here MUST be applied identically in the other repo. A parity test
 *  in each repo checksums `buildChartOption` over shared fixtures and asserts
 *  the same constant, so the two copies cannot silently drift.
 *
 *  Dispatch is registry-driven: GEOMETRY maps each ChartSpec `kind` to its
 *  builder + canvas size, and EXTRACT maps each `chart_type` to the extractor
 *  that builds its spec. New geometries are added as one registry entry each —
 *  see the union and the maps below.
 *
 *  PURE-JSON RULE: the emitted option must carry NO functions — no formatter
 *  callbacks, no scriptable styles. Every label the chart shows (scores,
 *  cutoffs, gauge values) is precomputed here as a literal string, so both
 *  renderers draw the exact same text and the parity checksum stays honest.
 *  Do NOT add echarts runtime imports here: type-only import keeps this file
 *  pure and shareable.
 * ============================================================================
 */
import type { EChartsOption } from 'echarts';

// Default raster size (matches the backend renderers' historical DEFAULT_WIDTH/HEIGHT).
export const CHART_CANVAS = { width: 800, height: 450 } as const;

// Categorical palette led by a chart-legible índigo derived from the brand
// deep-blue (#01026b is too dark for series fills). Validated for colorblind
// separation (all-pairs CVD ΔE ≥ 12 on white); slot order is the safety
// mechanism, so assign in sequence and never re-sort.
export const PALETTE = ['#3D52D9', '#1BAF7A', '#EDA100', '#E34948', '#4A3AA7', '#E87BA4'] as const;

export const colorAt = (index: number): string => PALETTE[index % PALETTE.length];

// Darken a hex color toward black by `amount` (0–1). Used to derive extra series
// shades from the report's secondary brand color when a chart has more series
// than the two brand slots (primary, secondary). Pure + deterministic so the
// browser preview and the server raster bake identical values.
export const darken = (hex: string, amount: number): string => {
    const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(hex.trim());
    if (!match) return hex;
    let h = match[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const factor = Math.max(0, Math.min(1, 1 - amount));
    const channel = (i: number) =>
        Math.round(parseInt(h.slice(i, i + 2), 16) * factor)
            .toString(16)
            .padStart(2, '0');
    return `#${channel(0)}${channel(2)}${channel(4)}`;
};

// Expand a hex color to an `rgba(r,g,b,a)` string at the given alpha (0–1). Used
// to derive a classification-band ramp from a single base color; pure so the
// browser preview and the server raster bake identical fills.
export const hexToRgba = (hex: string, alpha: number): string => {
    const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(hex.trim());
    if (!match) return hex;
    let h = match[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const channel = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return `rgba(${channel(0)},${channel(2)},${channel(4)},${alpha})`;
};

// Each series past the second is a step darker than the one before it, all
// derived from the secondary brand color.
export const SECONDARY_SHADE_STEP = 0.15;

// Explicit font stack on every piece of chart text so the browser preview and
// the server raster (SSR measuring through node-canvas with the bundled
// Manrope) resolve the same metrics instead of each falling back to a
// different default.
export const CHART_FONT_FAMILY = "Manrope, 'Segoe UI', system-ui, sans-serif";

// De-emphasis gray for norm/reference series (população, grupo controle): the
// patient's series carries the accent; the norm is quiet context behind it.
export const REFERENCE_SERIES_COLOR = '#94A3B8';

const TITLE_INK = '#0F172A';
const LABEL_INK = '#334155';
const AXIS_TICK_COLOR = '#64748B';
const AXIS_GRID_COLOR = '#E9EDF4';

// Text-style fragments reused across every builder (ECharts label option keys).
const TICK_TEXT = { fontFamily: CHART_FONT_FAMILY, fontSize: 12, color: AXIS_TICK_COLOR } as const;
const DATA_LABEL_TEXT = {
    fontFamily: CHART_FONT_FAMILY,
    fontSize: 11,
    fontWeight: 600,
    color: LABEL_INK,
} as const;
const AXIS_NAME_TEXT = { fontFamily: CHART_FONT_FAMILY, fontSize: 12, color: LABEL_INK } as const;

/**
 * pt-BR number rendering for chart labels (decimal comma, no thousands
 * grouping — clinical scores never need it). Hand-rolled instead of
 * `toLocaleString` so the string is identical across every JS engine/ICU the
 * two mirrored cores run on: the formatted text is INSIDE the hashed option.
 */
export const formatPtBr = (value: number): string => {
    if (!Number.isFinite(value)) return '';
    return String(value).replace('.', ',');
};

/** Marker shapes a series can carry (stored names; mapped to ECharts symbols). */
export type SeriesSymbol = 'circle' | 'square' | 'diamond' | 'triangle';

const SYMBOL_MAP: Record<SeriesSymbol, string> = {
    circle: 'circle',
    square: 'rect',
    diamond: 'diamond',
    triangle: 'triangle',
};

// Auto-cycle for multi-series line/scatter: distinct shapes keep series
// tellable-apart in grayscale print and under color-vision deficiency (the
// CBCL multi-informant convention). Explicit per-series symbols win.
const SERIES_SYMBOLS: SeriesSymbol[] = ['circle', 'square', 'diamond', 'triangle'];

const seriesSymbol = (spec: CategoricalSeriesSpec, series: ChartSeries, index: number): string => {
    if (series.symbol && SYMBOL_MAP[series.symbol]) return SYMBOL_MAP[series.symbol];
    // Reference series stay circles — the dashed gray line already sets them apart.
    if (series.role === 'reference') return 'circle';
    if (spec.series.length > 1) return SYMBOL_MAP[SERIES_SYMBOLS[index % SERIES_SYMBOLS.length]];
    return 'circle';
};

export interface ChartSeries {
    label: string;
    values: number[];
    /** Optional explicit color; otherwise the series falls back to the
     *  primary color (first series) or the palette. */
    color?: string;
    /** 'reference' = norm/população context: gray, dashed, behind the
     *  patient series, no point labels. */
    role?: 'reference';
    /** Optional explicit marker shape; otherwise multi-series line/scatter
     *  auto-cycle shapes for CVD/print safety. */
    symbol?: SeriesSymbol;
}

/**
 * Implemented now: one or more series sharing a categorical x-axis.
 * `series` holds exactly one entry in Step 1, but the shape is multi-series
 * ready so CBCL/Big-Five style charts drop in without an interface change.
 */
export interface CategoricalSeriesSpec {
    kind: 'categorical-series';
    chartType: 'bar' | 'line' | 'radar' | 'scatter';
    labels: string[];
    series: ChartSeries[];
    primaryColor?: string;
    secondaryColor?: string;
    title?: string;
    /** Shaded classification bands, drawn UNDER the data (cartesian only). */
    referenceBands?: { min: number; max: number; color?: string; label?: string }[];
    /** Horizontal threshold lines, drawn OVER the data (cartesian only). */
    cutoffLines?: { value: number; color?: string; label?: string }[];
    /** Force the y-scale bounds so bands/cutoffs stay on-screen. */
    yAxis?: { min?: number; max?: number };
    /** Line-chart smoothing (default 'smooth' → smooth 0.4). */
    curve?: 'straight' | 'smooth';
    /** Fill the area under a line chart (default false). */
    fillArea?: boolean;
    /** Per-point score labels (clinical convention; default true). */
    pointLabels?: boolean;
    /** Two-level x-axis: ordered domain groups over the leaf categories
     *  (CBCL "Comportamento/Escalas DSM", Big-Five domains). Spans must sum
     *  to `labels.length`; rendered as a second offset category axis. */
    categoryGroups?: { label: string; span: number }[];
    /** Single-series bar + categoryGroups: color each bar by its group
     *  (Vineland-style domain color families) instead of one series color. */
    colorByGroup?: boolean;
    /** Bar appearance overrides; absent = today's soft-rounded gradient bars. */
    barStyle?: {
        corner?: 'square' | 'soft' | 'round';
        thickness?: 'thin' | 'normal' | 'thick';
        gradient?: boolean;
    };
    /** Area-fill fade for line charts (default true); false = flat fill. */
    fillGradient?: boolean;
    /** Manual per-category fill overrides keyed by x label (single series). */
    pointColors?: Record<string, string>;
    /** Tint each mark by the reference band containing its value (single series). */
    colorByClassification?: boolean;
    /** Emphasize one category: dark ring + bold label (+ optional fill override). */
    highlight?: { label: string; color?: string };
    /** Test–retest evolution: "▲ +12 / ▼ −5" labels above each category pair
     *  (second series − first series; exactly 2 data series). */
    seriesDeltas?: boolean;
    /** Confidence half-widths aligned to `labels` (single series): line charts
     *  draw a shaded ± band, bar/scatter draw whiskers. */
    margins?: number[];
}

/**
 * True {x,y} scatter: each point pairs two numeric columns (unlike the
 * categorical fake-scatter, which plots values over an index). Optional quadrant
 * dividers (a vertical + horizontal line) split the plane into clinical
 * quadrants, rendered as dashed markLines.
 */
export interface XyScatterSpec {
    kind: 'xy-scatter';
    points: { x: number; y: number; label?: string }[];
    xLabel?: string;
    yLabel?: string;
    primaryColor?: string;
    title?: string;
    /** Vertical divider on the x scale. */
    quadrantX?: number;
    /** Horizontal divider on the y scale. */
    quadrantY?: number;
    /** Semantic labels at the axis extremes (lento/rápido, impreciso/cuidadoso)
     *  — the clinical quadrant-plane convention. */
    endLabels?: { xMin?: string; xMax?: string; yMin?: string; yMax?: string };
}

/**
 * Score(s) placed on the standard normal curve: a gaussian bell curve, fixed
 * classification bands (in z units) and one dashed vertical marker per score.
 * Each score is a numeric column value converted to a z-score by its metric
 * (T / IQ / percentile / …); non-standardized metrics fall back to the raw
 * value (documented weak default — distribution expects a standardized column).
 */
export interface DistributionSpec {
    kind: 'distribution';
    /** One dashed marker per score, positioned on the z axis. */
    markers: { z: number; label?: string }[];
    /** Classification bands in z units, drawn as vertical boxes under the curve. */
    bands: { min: number; max: number; color?: string; label?: string }[];
    /** Inclusive z-axis bounds for the curve + scale. */
    domain: [number, number];
    primaryColor?: string;
    title?: string;
    /** Optional z-axis title override. */
    xLabel?: string;
}

/**
 * A single value on an arc/track — ECharts' native gauge series: colored zones
 * (bands, in value units) paint the outer scale, a round-capped progress arc
 * fills from `min` to `value`, and the value renders as the precomputed
 * `detail` string (pure JSON, no draw callbacks).
 */
export interface GaugeSpec {
    kind: 'gauge';
    value: number;
    min: number;
    max: number;
    /** Colored zones across [min,max], drawn as the outer arc. */
    bands: { min: number; max: number; color?: string; label?: string }[];
    primaryColor?: string;
    title?: string;
    /** Optional unit suffix appended to the displayed value (e.g. "%"). */
    unit?: string;
}

/**
 * A single value on a horizontal level bar (bullet chart): the measure bar fills
 * from `min` to `value` over the scale, with optional colored classification
 * zones (bands, in value units) behind it. Same data contract as `GaugeSpec`,
 * rendered linearly instead of radially — the per-trait indicator used across
 * personality/symptom reports.
 */
export interface BulletSpec {
    kind: 'bullet';
    value: number;
    min: number;
    max: number;
    /** Colored zones across [min,max], drawn behind the measure bar. */
    bands: { min: number; max: number; color?: string; label?: string }[];
    primaryColor?: string;
    title?: string;
    /** Optional unit suffix appended to the displayed value (e.g. "%"). */
    unit?: string;
}

/**
 * One classification level bar PER ROW (the report archetype for Vineland
 * domains / Big Five facets): each row's measure floats from `min` to its
 * value over shared classification zones. The multi-row sibling of
 * `BulletSpec` — same zones/scale contract, one bar per row instead of one
 * chart per row.
 */
export interface LevelBarsSpec {
    kind: 'level-bars';
    rows: { label: string; value: number }[];
    min: number;
    max: number;
    /** Colored zones across [min,max], drawn behind the bars. */
    bands: { min: number; max: number; color?: string; label?: string }[];
    primaryColor?: string;
    title?: string;
    /** Optional unit suffix appended to the value labels (e.g. "%"). */
    unit?: string;
}

/**
 * Reserved seam — added just-in-time as each clinical chart is built:
 *   | { kind: 'forest'; ... }          // estimate + confidence interval
 *   | { kind: 'dumbbell'; ... }        // pre/post comparison
 */
export type ChartSpec =
    | CategoricalSeriesSpec
    | XyScatterSpec
    | DistributionSpec
    | GaugeSpec
    | BulletSpec
    | LevelBarsSpec;

const seriesColor = (spec: CategoricalSeriesSpec, series: ChartSeries, index: number): string => {
    if (series.color) return series.color;
    if (series.role === 'reference') return REFERENCE_SERIES_COLOR;
    if (index === 0 && spec.primaryColor) return spec.primaryColor;
    if (index === 1 && spec.secondaryColor) return spec.secondaryColor;
    if (index >= 2 && spec.secondaryColor)
        return darken(spec.secondaryColor, SECONDARY_SHADE_STEP * (index - 1));
    return colorAt(index);
};

/* ----------------------------------------------------------------------------
 *  Shared ECharts fragments (all plain data).
 * ------------------------------------------------------------------------- */

// A subtle vertical fade — the only place gradients are allowed is FILLS
// (bars, areas); strokes and text always stay solid.
const verticalGradient = (from: string, to: string) => ({
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
        { offset: 0, color: from },
        { offset: 1, color: to },
    ],
});

const titleComponent = (title?: string) =>
    title
        ? {
              text: title,
              left: 'center',
              top: 8,
              textStyle: {
                  fontFamily: CHART_FONT_FAMILY,
                  fontSize: 17,
                  fontWeight: 600,
                  color: TITLE_INK,
              },
          }
        : undefined;

// Legend only when there is something to tell apart: a single series is named
// by the title, so a one-swatch legend box would just restate it.
const legendComponent = (seriesCount: number) =>
    seriesCount > 1
        ? {
              show: true,
              bottom: 0,
              left: 'center',
              icon: 'circle',
              itemWidth: 8,
              itemHeight: 8,
              itemGap: 16,
              textStyle: { fontFamily: CHART_FONT_FAMILY, fontSize: 12, color: LABEL_INK },
          }
        : undefined;

// Quiet white-chip label shared by every decoration (bands, cutoffs, markers)
// so classification text stays legible over any fill without shouting. The
// text itself is baked in as a literal `formatter` string (pure-JSON rule).
const chipLabel = (text: string, position: string) => ({
    show: true,
    position,
    formatter: text,
    // ECharts rotates some mark labels to follow their line; chips must stay
    // horizontal everywhere.
    rotate: 0,
    ...DATA_LABEL_TEXT,
    backgroundColor: 'rgba(255,255,255,0.85)',
    padding: [3, 6],
    borderRadius: 4,
});

// Per-point score labels: the series opts in with this fragment and each datum
// carries its own precomputed pt-BR string (see labeledData).
const pointLabelFragment = (position: 'top' | 'right') => ({
    show: true,
    position,
    distance: 4,
    ...DATA_LABEL_TEXT,
});

// Data items with per-datum label text baked in. ECharts' default label would
// print raw JS numbers ("55.5"); the formatter literal pins pt-BR ("55,5") in
// both renderers without a callback.
const labeledData = (values: number[]) =>
    values.map((value) => ({ value, label: { formatter: formatPtBr(value) } }));

// Defaults when a decoration omits its color: a faint índigo-tinted fill for
// bands, a solid warm stroke for cutoff lines.
const DEFAULT_BAND_COLOR = 'rgba(61,82,217,0.07)';
const DEFAULT_CUTOFF_COLOR = '#D6491F';

/** Reference band containing a value (first match wins; bounds inclusive). */
const bandForValue = (
    bands: { min: number; max: number; color?: string }[] | undefined,
    value: number,
) => bands?.find((band) => value >= band.min && value <= band.max);

// Band fills are translucent backgrounds; classification-colored marks need the
// same hue at full strength. rgba() → rgb() at alpha 1; hex passes through.
const solidifyBandColor = (color?: string): string => {
    if (!color) return '#3D52D9';
    const match = color.match(/^rgba?\(([^)]+)\)$/);
    if (!match) return color;
    const [r, g, b] = match[1].split(',').map((part) => part.trim());
    return `rgb(${r},${g},${b})`;
};

// Highlight chrome shared by every mark type: a dark ring + bold score label.
const HIGHLIGHT_RING = { borderColor: '#0f172a', borderWidth: 2 };

/**
 * Per-datum item list for a SINGLE data series when any per-point override
 * (manual colors, classification tint, highlight) applies; null otherwise so
 * untouched charts keep their plain data arrays. `wrapFill` lets bars reuse
 * their gradient treatment on the resolved base color.
 */
const perPointOverrides = (
    spec: CategoricalSeriesSpec,
    values: number[],
    withLabels: boolean,
    wrapFill: (base: string) => unknown = (base) => base,
    // Bars have no symbols; marks (line/scatter) grow the highlighted point.
    symbolSized = true,
): Record<string, unknown>[] | null => {
    if (spec.series.length !== 1) return null;
    if (!spec.pointColors && !spec.colorByClassification && !spec.highlight) return null;
    return values.map((value, i) => {
        const label = spec.labels[i] ?? '';
        const highlighted = !!spec.highlight && label === spec.highlight.label;
        const manual = spec.pointColors?.[label];
        const band =
            !manual && spec.colorByClassification
                ? bandForValue(spec.referenceBands, value)
                : undefined;
        const base =
            highlighted && spec.highlight?.color
                ? spec.highlight.color
                : (manual ?? (band ? solidifyBandColor(band.color) : undefined));
        const itemStyle = {
            ...(base ? { color: wrapFill(base) } : {}),
            ...(highlighted ? HIGHLIGHT_RING : {}),
        };
        return {
            value,
            ...(Object.keys(itemStyle).length ? { itemStyle } : {}),
            ...(highlighted && symbolSized ? { symbolSize: 14 } : {}),
            ...(withLabels || highlighted
                ? {
                      label: {
                          formatter: formatPtBr(value),
                          ...(highlighted ? { fontWeight: 'bold' } : {}),
                      },
                  }
                : {}),
        };
    });
};

// markArea entries for classification bands. `axis` picks which value axis the
// band spans (y = horizontal band behind cartesian data, x = vertical zone).
// Per-band style/label ride the range's FIRST element, per the markArea shape.
const bandMarkAreaData = (
    bands: { min: number; max: number; color?: string; label?: string }[],
    axis: 'x' | 'y',
    labelPosition: string,
    defaultColorAt: (index: number) => string,
) =>
    bands.map((band, index) => [
        {
            [`${axis}Axis`]: band.min,
            itemStyle: { color: band.color || defaultColorAt(index) },
            ...(band.label ? { label: chipLabel(band.label, labelPosition) } : {}),
        },
        { [`${axis}Axis`]: band.max },
    ]);

// Chip text is 11px; used to reserve margin room for outside-placed chips.
const CHIP_CHAR_W = 6.2;
const chipWidth = (text: string): number => Math.ceil(text.length * CHIP_CHAR_W) + 20;

// Widest decoration label, or 0 when none carries a label. Decoration chips
// render OUTSIDE the plot's right edge — inside the plot they inevitably
// collide with data (and the decoration series draws under it) — so the grid's
// right margin must reserve room for the widest one.
const decorationInset = (spec: CategoricalSeriesSpec): number => {
    const texts = [
        ...(spec.referenceBands ?? []).map((band) => band.label),
        ...(spec.cutoffLines ?? []).map((cutoff) => cutoff.label),
    ].filter((label): label is string => Boolean(label));
    return texts.length === 0 ? 0 : Math.max(...texts.map(chipWidth));
};

// One silent, dataless series carrying every decoration (bands under the data
// via z:0, cutoffs over it via the markLine). Keeping decorations OFF the data
// series means toggling them can never disturb series styling, and their draw
// order is deterministic. Labels sit outside the right edge, vertically at the
// band's center / on the cutoff line — the clinical zone-annotation convention.
const decorationSeries = (spec: CategoricalSeriesSpec): Record<string, unknown> | undefined => {
    const bands = spec.referenceBands ?? [];
    const cutoffs = spec.cutoffLines ?? [];
    if (bands.length === 0 && cutoffs.length === 0) return undefined;

    const series: Record<string, unknown> = { type: 'line', data: [], silent: true, z: 0 };
    if (bands.length > 0) {
        series.markArea = {
            silent: true,
            data: bandMarkAreaData(bands, 'y', 'right', () => DEFAULT_BAND_COLOR),
        };
    }
    if (cutoffs.length > 0) {
        series.markLine = {
            silent: true,
            symbol: 'none',
            data: cutoffs.map((cutoff) => ({
                yAxis: cutoff.value,
                lineStyle: {
                    color: cutoff.color || DEFAULT_CUTOFF_COLOR,
                    type: [6, 4],
                    width: 1.5,
                },
                label: cutoff.label ? chipLabel(cutoff.label, 'end') : { show: false },
            })),
        };
    }
    return series;
};

/* ----------------------------------------------------------------------------
 *  Data-adaptive axis bounds.
 *
 *  An axis bound — forced by the user or defaulted by a geometry — must never
 *  CLIP or saturate the data: a score past the scale end used to draw over the
 *  plot top (categorical) or silently pin at max (gauge/bullet/level bars).
 *  Instead the bound expands OUTWARD to the next "nice" number. Pure math,
 *  parity-safe.
 * ------------------------------------------------------------------------- */

const NICE_AXIS_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/** Smallest nice bound (step × 10^k, ~5% headroom) at or above `value`; 0 when non-positive. */
const niceCeil = (value: number): number => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const padded = value * 1.05;
    const magnitude = 10 ** Math.floor(Math.log10(padded));
    const step = NICE_AXIS_STEPS.find((s) => s * magnitude >= padded) ?? 10;
    return step * magnitude;
};

/** Largest nice bound at or below `value` (~5% headroom); mirrors niceCeil below zero. */
const niceFloor = (value: number): number => {
    if (!Number.isFinite(value) || value === 0) return 0;
    if (value < 0) return -niceCeil(-value);
    const padded = value * 0.95;
    const magnitude = 10 ** Math.floor(Math.log10(padded));
    const fitting = NICE_AXIS_STEPS.filter((s) => s * magnitude <= padded);
    return (fitting[fitting.length - 1] ?? 1) * magnitude;
};

/** The bound itself, or the next nice number past the data when it falls outside. */
const expandedMax = (bound: number, dataMax: number): number =>
    dataMax > bound ? Math.max(niceCeil(dataMax), dataMax) : bound;
const expandedMin = (bound: number, dataMin: number): number =>
    dataMin < bound ? Math.min(niceFloor(dataMin), dataMin) : bound;

/* ----------------------------------------------------------------------------
 *  Deterministic layout.
 *
 *  Every option pins an explicit `grid` (containLabel:false) computed from the
 *  FIXED logical canvas, so macro-layout never depends on runtime text
 *  measurement — the SSR raster and the browser preview lay out identically
 *  even if their text metrics differ by a fraction of a pixel. Label room is
 *  ESTIMATED from character counts (Manrope 12px averages ~6.8px/char); the
 *  estimate only pads margins, so a miss costs whitespace, never clipping of
 *  the plot itself.
 * ------------------------------------------------------------------------- */

const CHAR_W = 6.8;
const GRID_LEFT = 56;
const GRID_RIGHT = 24;

// Rotate categorical tick labels 45° when the longest label would overflow its
// slot; the bottom margin then grows by the label's projected height.
const categoryRotation = (labels: string[], width: number): number => {
    const usable = width - GRID_LEFT - GRID_RIGHT;
    const slot = usable / Math.max(1, labels.length);
    const maxLen = labels.reduce((acc, label) => Math.max(acc, label.length), 0);
    return maxLen * CHAR_W + 8 > slot ? 45 : 0;
};

// Height of the leaf tick-label band under the plot (grows when rotated).
const leafLabelBand = (spec: CategoricalSeriesSpec, width: number): number => {
    const rotate = categoryRotation(spec.labels, width);
    const maxLen = spec.labels.reduce((acc, label) => Math.max(acc, label.length), 0);
    return rotate ? 20 + Math.min(110, Math.ceil(maxLen * CHAR_W * 0.7071)) : 34;
};

// Height of the domain-group header row (second x-axis), when present.
const GROUP_BAND = 26;

const categoricalGrid = (
    spec: CategoricalSeriesSpec,
    width: number,
): { left: number; right: number; top: number; bottom: number; containLabel: false } => {
    const labelBand = leafLabelBand(spec, width);
    const groupBand = spec.categoryGroups?.length ? GROUP_BAND : 0;
    const legendBand = spec.series.length > 1 ? 30 : 0;
    return {
        left: GRID_LEFT,
        right: GRID_RIGHT,
        // Room for the title block plus end-anchored score labels at the top.
        top: spec.title ? 56 : 30,
        bottom: labelBand + groupBand + legendBand,
        containLabel: false,
    };
};

/* ----------------------------------------------------------------------------
 *  Two-level categorical x-axis (domain groups over leaf categories).
 *
 *  The group row is a SECOND category axis offset below the leaf tick band:
 *  each group's label occupies its center slot, every other slot is empty.
 *  Vertical separators between groups are `graphic` lines at deterministic px
 *  (the fixed grid makes the slot math exact) — pure JSON, no callbacks.
 * ------------------------------------------------------------------------- */

const validCategoryGroups = (spec: CategoricalSeriesSpec): { label: string; span: number }[] => {
    const groups = spec.categoryGroups ?? [];
    if (groups.length === 0) return [];
    const total = groups.reduce((acc, group) => acc + Math.max(0, Math.floor(group.span)), 0);
    // Stale spans (table edited since grouping was stored) would misalign every
    // header; dropping the row degrades gracefully to the plain axis.
    return total === spec.labels.length ? groups : [];
};

const groupAxisData = (groups: { label: string; span: number }[], total: number): string[] => {
    const slots: string[] = Array.from({ length: total }, () => '');
    let start = 0;
    for (const group of groups) {
        slots[start + Math.floor((group.span - 1) / 2)] = group.label;
        start += group.span;
    }
    return slots;
};

const groupSeparators = (
    groups: { label: string; span: number }[],
    grid: { left: number; right: number; top: number; bottom: number },
): Record<string, unknown>[] => {
    const plotWidth = CHART_CANVAS.width - grid.left - grid.right;
    const slot =
        plotWidth /
        Math.max(
            1,
            groups.reduce((acc, g) => acc + g.span, 0),
        );
    const lines: Record<string, unknown>[] = [];
    let boundary = 0;
    for (const group of groups.slice(0, -1)) {
        boundary += group.span;
        const x = Math.round(grid.left + boundary * slot) + 0.5;
        lines.push({
            type: 'line',
            silent: true,
            shape: {
                x1: x,
                y1: grid.top,
                x2: x,
                // Reach into the label band so the boundary reads across both rows.
                y2: CHART_CANVAS.height - grid.bottom + 6,
            },
            style: { stroke: AXIS_GRID_COLOR, lineWidth: 1 },
        });
    }
    return lines;
};

/* ----------------------------------------------------------------------------
 *  Geometry builders — one per ChartSpec kind, each returning a pure-JSON
 *  EChartsOption. `animation:false` is part of the mirrored option so the SSR
 *  raster is deterministic; the browser renderer re-enables animation OUTSIDE
 *  the hashed option.
 * ------------------------------------------------------------------------- */

const buildCategoricalSeriesOption = (spec: CategoricalSeriesSpec): EChartsOption => {
    const labels = spec.labels;
    const showPointLabels = spec.pointLabels !== false;
    const values = spec.series.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
    const dataMin = values.length ? Math.min(...values) : undefined;
    const dataMax = values.length ? Math.max(...values) : undefined;

    if (spec.chartType === 'radar') {
        // Radar needs explicit indicator bounds; derive nice ones from the data
        // (or the forced yAxis) so the outer ring is never a raw data value.
        const rMax =
            spec.yAxis?.max !== undefined
                ? expandedMax(spec.yAxis.max, dataMax ?? spec.yAxis.max)
                : niceCeil(dataMax ?? 100) || 100;
        const rMin =
            spec.yAxis?.min !== undefined
                ? expandedMin(spec.yAxis.min, dataMin ?? spec.yAxis.min)
                : Math.min(0, niceFloor(dataMin ?? 0));
        // ECharts does not wrap indicator names, so the radius shrinks to leave
        // the longest name room to render whole (same square-size expression as
        // this geometry's resolveSize).
        const size = Math.min(CHART_CANVAS.width, CHART_CANVAS.height * 1.1);
        const maxNameLen = labels.reduce((acc, label) => Math.max(acc, label.length), 0);
        const radius = Math.round(
            Math.max(90, Math.min(0.34 * size, size / 2 - maxNameLen * CHAR_W * 0.85 - 20)),
        );
        return {
            animation: false,
            ...(spec.title ? { title: titleComponent(spec.title) } : {}),
            ...(spec.series.length > 1 ? { legend: legendComponent(spec.series.length) } : {}),
            radar: {
                indicator: labels.map((name) => ({ name, min: rMin, max: rMax })),
                center: ['50%', spec.title ? '56%' : '52%'],
                radius,
                axisName: { ...TICK_TEXT, color: LABEL_INK },
                axisLine: { lineStyle: { color: AXIS_GRID_COLOR } },
                splitLine: { lineStyle: { color: AXIS_GRID_COLOR } },
                splitArea: { show: false },
            },
            series: [
                {
                    type: 'radar',
                    data: spec.series.map((s, i) => {
                        const color = seriesColor(spec, s, i);
                        return {
                            value: s.values,
                            name: s.label,
                            itemStyle: { color, borderColor: '#ffffff', borderWidth: 2 },
                            lineStyle: {
                                width: 2,
                                color,
                                ...(s.role === 'reference' ? { type: [5, 4] } : {}),
                            },
                            areaStyle: { color: color + '26' },
                            symbol: 'circle',
                            symbolSize: 8,
                        };
                    }),
                },
            ],
        } as EChartsOption;
    }

    // Optional forced y-scale bounds — expanded so out-of-range data can never
    // draw past the plot edge. Without a forced bound the scale starts at zero
    // (clinical convention) unless the data itself goes negative.
    let yMin: number | undefined = dataMin !== undefined && dataMin < 0 ? undefined : 0;
    let yMax: number | undefined;
    if (spec.yAxis?.min !== undefined) {
        yMin = dataMin !== undefined ? expandedMin(spec.yAxis.min, dataMin) : spec.yAxis.min;
    }
    if (spec.yAxis?.max !== undefined) {
        yMax = dataMax !== undefined ? expandedMax(spec.yAxis.max, dataMax) : spec.yAxis.max;
    }

    const grid = categoricalGrid(spec, CHART_CANVAS.width);
    grid.right += decorationInset(spec);
    const rotate = categoryRotation(labels, CHART_CANVAS.width);
    // Integer ticks whenever the data is integer — the common clinical case —
    // so value-axis labels never need locale decimal formatting.
    const allIntegers = values.every((v) => Number.isInteger(v));

    const leafAxis = {
        type: 'category' as const,
        data: labels,
        boundaryGap: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { ...TICK_TEXT, interval: 0, ...(rotate ? { rotate } : {}) },
        splitLine:
            spec.chartType === 'scatter'
                ? { show: true, lineStyle: { color: AXIS_GRID_COLOR } }
                : { show: false },
    };
    const groups = validCategoryGroups(spec);
    const xAxis = groups.length
        ? [
              leafAxis,
              {
                  type: 'category' as const,
                  position: 'bottom' as const,
                  // The group header row sits below the leaf tick band.
                  offset: leafLabelBand(spec, CHART_CANVAS.width),
                  data: groupAxisData(groups, labels.length),
                  boundaryGap: true,
                  axisLine: { show: false },
                  axisTick: { show: false },
                  splitLine: { show: false },
                  axisLabel: { ...TICK_TEXT, color: LABEL_INK, fontWeight: 600, interval: 0 },
              },
          ]
        : leafAxis;
    const yAxis = {
        type: 'value' as const,
        ...(yMin !== undefined ? { min: yMin } : {}),
        ...(yMax !== undefined ? { max: yMax } : {}),
        ...(allIntegers ? { minInterval: 1 } : {}),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: TICK_TEXT,
        splitLine: { show: true, lineStyle: { color: AXIS_GRID_COLOR } },
    };

    const series: Record<string, unknown>[] = spec.series.map((s, i) => {
        const color = seriesColor(spec, s, i);
        const isReference = s.role === 'reference';
        const withLabels = showPointLabels && !isReference;
        const data = withLabels ? labeledData(s.values) : s.values;

        if (spec.chartType === 'bar') {
            // Thickness cap grows when few bars share the plot and shrinks back
            // as categories multiply; bars never fill their slot — the leftover
            // air keeps groups readable. The user's thickness preset scales the
            // cap; corners and gradient default to today's soft/gradient look.
            const barCount = Math.max(1, labels.length * spec.series.length);
            const thicknessFactor =
                spec.barStyle?.thickness === 'thin'
                    ? 0.55
                    : spec.barStyle?.thickness === 'thick'
                      ? 1.5
                      : 1;
            const barMaxWidth = Math.round(
                Math.max(28, Math.min(64, Math.floor(520 / barCount))) * thicknessFactor,
            );
            const cornerRadius =
                spec.barStyle?.corner === 'square'
                    ? [0, 0, 0, 0]
                    : spec.barStyle?.corner === 'round'
                      ? [10, 10, 0, 0]
                      : [4, 4, 0, 0];
            const flatFill = spec.barStyle?.gradient === false;
            const barFill = (base: string) =>
                flatFill ? base : verticalGradient(base, darken(base, 0.08));
            // Vineland-style domain color families: a single grouped series
            // colors each bar by its group instead of the series color.
            const groupFill =
                spec.colorByGroup && groups.length && spec.series.length === 1
                    ? labels.map((_, labelIndex) => {
                          let start = 0;
                          let groupIndex = 0;
                          for (const [gi, group] of groups.entries()) {
                              if (labelIndex < start + group.span) {
                                  groupIndex = gi;
                                  break;
                              }
                              start += group.span;
                          }
                          const groupColor = colorAt(groupIndex);
                          return barFill(groupColor);
                      })
                    : null;
            // Per-datum precedence: manual point color > classification band
            // tint > group family > series color.
            const overrides = isReference
                ? null
                : perPointOverrides(spec, s.values, withLabels, barFill, false);
            const barData = overrides
                ? overrides.map((item, i) =>
                      groupFill && !(item.itemStyle as { color?: unknown } | undefined)?.color
                          ? {
                                ...item,
                                itemStyle: {
                                    ...(item.itemStyle as object),
                                    color: groupFill[i],
                                },
                            }
                          : item,
                  )
                : groupFill
                  ? s.values.map((value, i) => ({
                        value,
                        itemStyle: { color: groupFill[i] },
                        ...(withLabels ? { label: { formatter: formatPtBr(value) } } : {}),
                    }))
                  : data;
            return {
                type: 'bar',
                name: s.label,
                data: barData,
                barMaxWidth,
                itemStyle: {
                    borderRadius: cornerRadius,
                    ...(groupFill ? {} : { color: barFill(color) }),
                },
                ...(withLabels ? { label: pointLabelFragment('top') } : {}),
                ...(isReference ? { z: 1 } : {}),
            };
        }
        if (spec.chartType === 'scatter') {
            return {
                type: 'scatter',
                name: s.label,
                data: (!isReference && perPointOverrides(spec, s.values, withLabels)) || data,
                symbol: seriesSymbol(spec, s, i),
                symbolSize: isReference ? 8 : 10,
                itemStyle: {
                    color,
                    borderColor: '#ffffff',
                    borderWidth: 2,
                    shadowBlur: 4,
                    shadowColor: 'rgba(15,23,42,0.18)',
                },
                ...(withLabels ? { label: pointLabelFragment('top') } : {}),
                ...(isReference ? { z: 1 } : {}),
            };
        }
        // line (default)
        return {
            type: 'line',
            name: s.label,
            data:
                (spec.chartType === 'line' &&
                    !isReference &&
                    perPointOverrides(spec, s.values, withLabels)) ||
                data,
            smooth: spec.curve === 'straight' ? false : 0.4,
            symbol: seriesSymbol(spec, s, i),
            symbolSize: isReference ? 6 : 8,
            lineStyle: {
                width: 2.5,
                color,
                cap: 'round',
                join: 'round',
                ...(isReference ? { type: [5, 4] } : {}),
            },
            itemStyle: { color, borderColor: '#ffffff', borderWidth: 2 },
            ...(spec.fillArea && !isReference
                ? {
                      areaStyle: {
                          color:
                              spec.fillGradient === false
                                  ? hexToRgba(color, 0.16)
                                  : verticalGradient(
                                        hexToRgba(color, 0.22),
                                        hexToRgba(color, 0.02),
                                    ),
                      },
                  }
                : {}),
            ...(withLabels
                ? { label: pointLabelFragment('top'), labelLayout: { hideOverlap: true } }
                : {}),
            ...(isReference ? { z: 1 } : {}),
        };
    });

    const decorations = decorationSeries(spec);
    if (decorations) series.push(decorations);

    const deltas = deltaLabelSeries(spec);
    if (deltas) series.push(deltas);

    attachConfidence(spec, series);

    const separators = groups.length ? groupSeparators(groups, grid) : [];

    return {
        animation: false,
        ...(spec.title ? { title: titleComponent(spec.title) } : {}),
        ...(spec.series.length > 1 ? { legend: legendComponent(spec.series.length) } : {}),
        grid,
        xAxis,
        yAxis,
        series,
        ...(separators.length ? { graphic: { elements: separators } } : {}),
    } as EChartsOption;
};

// Evolution labels between exactly two data series: "▲ +12" (green) / "▼ −5"
// (red) / "=" (slate) above each category pair, drawn by a mark-less scatter
// series whose label strings are precomputed (pure-JSON rule).
const DELTA_UP_COLOR = '#2E7D32';
const DELTA_DOWN_COLOR = '#C62828';
const DELTA_ZERO_COLOR = '#64748B';

const deltaLabelSeries = (spec: CategoricalSeriesSpec): Record<string, unknown> | null => {
    if (!spec.seriesDeltas) return null;
    if (spec.chartType !== 'bar' && spec.chartType !== 'line') return null;
    const dataSeries = spec.series.filter((s) => s.role !== 'reference');
    if (dataSeries.length !== 2) return null;
    const [first, second] = dataSeries;
    return {
        type: 'scatter',
        silent: true,
        symbolSize: 0,
        z: 4,
        data: spec.labels.map((_, i) => {
            const before = first.values[i] ?? 0;
            const after = second.values[i] ?? 0;
            const delta = after - before;
            const text =
                delta > 0 ? `▲ +${formatPtBr(delta)}` : delta < 0 ? `▼ ${formatPtBr(delta)}` : '=';
            const color =
                delta > 0 ? DELTA_UP_COLOR : delta < 0 ? DELTA_DOWN_COLOR : DELTA_ZERO_COLOR;
            return {
                value: [i, Math.max(before, after)],
                label: {
                    show: true,
                    position: 'top',
                    distance: 16,
                    formatter: text,
                    ...DATA_LABEL_TEXT,
                    color,
                    fontWeight: 'bold',
                },
            };
        }),
    };
};

// Confidence intervals (single data series): line charts draw a shaded ± band
// via the stacked-area trick; bar/scatter draw vertical whiskers as markLine
// coord pairs. Both are plain data — no custom series, no callbacks.
const CI_WHISKER_COLOR = '#475569';

const attachConfidence = (spec: CategoricalSeriesSpec, series: Record<string, unknown>[]): void => {
    const margins = spec.margins;
    if (!margins?.length || spec.series.length !== 1) return;
    const s = spec.series[0];
    if (s.role === 'reference') return;
    const color = s.color || spec.primaryColor || colorAt(0);
    if (spec.chartType === 'line') {
        series.push(
            {
                type: 'line',
                silent: true,
                stack: 'ci-band',
                symbol: 'none',
                lineStyle: { opacity: 0 },
                data: s.values.map((value, i) => value - (margins[i] ?? 0)),
                z: 0,
            },
            {
                type: 'line',
                silent: true,
                stack: 'ci-band',
                symbol: 'none',
                lineStyle: { opacity: 0 },
                areaStyle: { color: hexToRgba(color, 0.16) },
                data: s.values.map((_, i) => 2 * (margins[i] ?? 0)),
                z: 0,
            },
        );
        return;
    }
    if (spec.chartType !== 'bar' && spec.chartType !== 'scatter') return;
    series[0].markLine = {
        silent: true,
        symbol: 'none',
        z: 3,
        lineStyle: { color: CI_WHISKER_COLOR, width: 1.5, type: 'solid' },
        label: { show: false },
        data: spec.labels.map((_, i) => [
            { coord: [i, (s.values[i] ?? 0) - (margins[i] ?? 0)] },
            { coord: [i, (s.values[i] ?? 0) + (margins[i] ?? 0)] },
        ]),
    };
};

// Quadrant dividers for xy-scatter: a vertical line on the x scale and/or a
// horizontal line on the y scale, drawn under the points. Plain data, so it
// stays parity-checksum-safe.
const QUADRANT_COLOR = 'rgba(61,82,217,0.35)';

const buildXyScatterOption = (spec: XyScatterSpec): EChartsOption => {
    const color = spec.primaryColor || colorAt(0);

    const series: Record<string, unknown>[] = [
        {
            type: 'scatter',
            name: spec.yLabel || spec.title || 'Pontos',
            data: spec.points.map((p) => [p.x, p.y]),
            symbol: 'circle',
            symbolSize: 14,
            itemStyle: {
                color,
                borderColor: '#ffffff',
                borderWidth: 2,
                shadowBlur: 4,
                shadowColor: 'rgba(15,23,42,0.18)',
            },
        },
    ];

    const quadrantLines: Record<string, unknown>[] = [];
    if (spec.quadrantX !== undefined) quadrantLines.push({ xAxis: spec.quadrantX });
    if (spec.quadrantY !== undefined) quadrantLines.push({ yAxis: spec.quadrantY });
    if (quadrantLines.length > 0) {
        series.push({
            type: 'line',
            data: [],
            silent: true,
            z: 1,
            markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { color: QUADRANT_COLOR, type: [6, 4], width: 2 },
                label: { show: false },
                data: quadrantLines,
            },
        });
    }

    const valueAxis = (name?: string) => ({
        type: 'value' as const,
        // Fit the axis to the data instead of forcing zero — clinical planes
        // (QI 70–130) would otherwise collapse into a corner.
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: TICK_TEXT,
        splitLine: { show: true, lineStyle: { color: AXIS_GRID_COLOR } },
        ...(name ? { name, nameLocation: 'middle' as const, nameTextStyle: AXIS_NAME_TEXT } : {}),
    });

    const ends = spec.endLabels ?? {};
    const hasXEnds = Boolean(ends.xMin || ends.xMax);
    const hasYEnds = Boolean(ends.yMin || ends.yMax);
    const grid = {
        left: GRID_LEFT + (spec.yLabel ? 16 : 0),
        right: 32,
        top: spec.title ? 56 : 32,
        bottom: (spec.xLabel ? 56 : 40) + (hasXEnds ? 16 : 0),
        containLabel: false as const,
    };

    // Semantic axis-end labels as graphic text at deterministic px — under the
    // x extremes and beside the y extremes, clear of ticks and axis names.
    const endFont = `600 11px ${CHART_FONT_FAMILY}`;
    const endText = (
        text: string,
        x: number,
        y: number,
        align: 'left' | 'right' | 'center',
    ): Record<string, unknown> => ({
        type: 'text',
        silent: true,
        x,
        y,
        style: { text, font: endFont, fill: AXIS_TICK_COLOR, textAlign: align },
    });
    const endElements: Record<string, unknown>[] = [];
    const xEndY = CHART_CANVAS.height - grid.bottom + 26;
    if (ends.xMin) endElements.push(endText(ends.xMin, grid.left, xEndY, 'left'));
    if (ends.xMax)
        endElements.push(endText(ends.xMax, CHART_CANVAS.width - grid.right, xEndY, 'right'));
    // The y extremes label INSIDE the plot corners (quiet, quadrant-label
    // style) — the outside-left margin belongs to the tick numbers, and a
    // quadrant plane is empty at its corners by construction.
    if (ends.yMax) endElements.push(endText(ends.yMax, grid.left + 10, grid.top + 8, 'left'));
    if (ends.yMin)
        endElements.push(
            endText(ends.yMin, grid.left + 10, CHART_CANVAS.height - grid.bottom - 24, 'left'),
        );

    return {
        animation: false,
        ...(spec.title ? { title: titleComponent(spec.title) } : {}),
        grid,
        xAxis: { ...valueAxis(spec.xLabel), ...(spec.xLabel ? { nameGap: 32 } : {}) },
        yAxis: { ...valueAxis(spec.yLabel), ...(spec.yLabel ? { nameGap: 40 } : {}) },
        series,
        ...(hasXEnds || hasYEnds ? { graphic: { elements: endElements } } : {}),
    } as EChartsOption;
};

/* ----------------------------------------------------------------------------
 *  Distribution geometry: score(s) placed on the standard normal curve.
 *
 *  The curve is generated from the gaussian density over a fixed z-domain;
 *  classification bands are vertical zones in z units; each score is a dashed
 *  vertical marker. The gaussian math (density + probit) is ported here so the
 *  core stays pure — no runtime import of the frontend stats lib.
 * ------------------------------------------------------------------------- */

// Standard normal probability density.
const normalDensity = (z: number): number => (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-(z * z) / 2);

// Inverse standard-normal CDF (probit): the z for a cumulative probability
// p∈(0,1). Acklam's rational approximation — deterministic, so it stays
// parity-safe across the two mirrored cores.
const inverseNormalCdf = (p: number): number => {
    if (p <= 0) return -4;
    if (p >= 1) return 4;
    const a1 = -39.6968302866538,
        a2 = 220.946098424521,
        a3 = -275.928510446969,
        a4 = 138.357751867269,
        a5 = -30.6647980661472,
        a6 = 2.50662827745924;
    const b1 = -54.4760987982241,
        b2 = 161.585836858041,
        b3 = -155.698979859887,
        b4 = 66.8013118877197,
        b5 = -13.2806815528857;
    const c1 = -0.00778489400243029,
        c2 = -0.322396458041136,
        c3 = -2.40075827716184,
        c4 = -2.54973253934373,
        c5 = 4.37466414146497,
        c6 = 2.93816398269878;
    const d1 = 0.00778469570904146,
        d2 = 0.32246712907004,
        d3 = 2.445134137143,
        d4 = 3.75440866190742;
    const pLow = 0.02425;
    const pHigh = 1 - pLow;
    if (p < pLow) {
        const q = Math.sqrt(-2 * Math.log(p));
        return (
            (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
            ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
        );
    }
    if (p <= pHigh) {
        const q = p - 0.5;
        const r = q * q;
        return (
            ((((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q) /
            (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1)
        );
    }
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return (
        -(((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
        ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
    );
};

// Round transcendental outputs to a fixed precision so the two mirrored cores
// checksum identically regardless of any last-ULP engine differences.
const roundZ = (n: number): number => Number(n.toFixed(6));

const clampToDomain = (z: number, [min, max]: [number, number]): number =>
    z < min ? min : z > max ? max : z;

// Default z-domain and the standard classification bands (in z units): a
// symmetric brand-tinted ramp, faint at the center and stronger at the tails.
const DISTRIBUTION_DOMAIN: [number, number] = [-3.4, 3.4];
const DENSITY_STEP = 0.1;
const DISTRIBUTION_MARKER_COLOR = '#D6491F';

// Neutral default so the classification regions read as quiet gray unless the
// professional picks a background color to derive tones from.
export const DEFAULT_DISTRIBUTION_BAND_COLOR = '#64748B';

// Classification stops (z ranges + labels + fill opacity): symmetric, faint at
// the center and stronger at the tails. The color is derived from one base hue.
const DISTRIBUTION_BAND_STOPS: { min: number; max: number; label: string; alpha: number }[] = [
    { min: -3.4, max: -2, label: 'Muito baixo', alpha: 0.16 },
    { min: -2, max: -1, label: 'Baixo', alpha: 0.1 },
    { min: -1, max: 1, label: 'Médio', alpha: 0.05 },
    { min: 1, max: 2, label: 'Alto', alpha: 0.1 },
    { min: 2, max: 3.4, label: 'Muito alto', alpha: 0.16 },
];

// The 5 classification bands, all tinted from a single base color, so the
// professional picks ONE color instead of five. Defaults to neutral gray.
export const buildDistributionBands = (
    baseColor: string = DEFAULT_DISTRIBUTION_BAND_COLOR,
): DistributionSpec['bands'] =>
    DISTRIBUTION_BAND_STOPS.map((stop) => ({
        min: stop.min,
        max: stop.max,
        label: stop.label,
        color: hexToRgba(baseColor, stop.alpha),
    }));

// z samples across [start,end] at `step`, endpoint included; each rounded so
// the accumulation never drifts (mirrors the reference SVG chart's buildRange).
const buildZRange = (start: number, end: number, step: number): number[] => {
    const values: number[] = [];
    for (let current = start; current < end; current += step) {
        values.push(Number(current.toFixed(4)));
    }
    values.push(Number(end.toFixed(4)));
    return values;
};

const buildDistributionOption = (spec: DistributionSpec): EChartsOption => {
    const [min, max] = spec.domain;
    const curveColor = spec.primaryColor || colorAt(0);
    const points = buildZRange(min, max, DENSITY_STEP).map((z) => [z, roundZ(normalDensity(z))]);

    const decoration: Record<string, unknown> = { type: 'line', data: [], silent: true, z: 0 };
    if (spec.bands.length > 0) {
        decoration.markArea = {
            silent: true,
            // Region labels stay at the TOP of each band so they never collide
            // with the score-marker labels anchored at the bottom.
            data: bandMarkAreaData(spec.bands, 'x', 'insideTop', () => DEFAULT_BAND_COLOR),
        };
    }
    if (spec.markers.length > 0) {
        decoration.markLine = {
            silent: true,
            symbol: 'none',
            data: spec.markers.map((marker) => ({
                xAxis: marker.z,
                lineStyle: { color: DISTRIBUTION_MARKER_COLOR, type: [6, 4], width: 1.5 },
                label: marker.label ? chipLabel(marker.label, 'insideStartTop') : { show: false },
            })),
        };
    }

    return {
        animation: false,
        ...(spec.title ? { title: titleComponent(spec.title) } : {}),
        grid: {
            left: 24,
            right: 24,
            top: spec.title ? 56 : 28,
            bottom: 44,
            containLabel: false,
        },
        xAxis: {
            type: 'value',
            min,
            max,
            // Raw z ticks mean nothing to report readers — the band chips carry
            // the meaning; only the axis name stays.
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            name: spec.xLabel || 'Escore padronizado (z)',
            nameLocation: 'middle',
            nameGap: 20,
            nameTextStyle: AXIS_NAME_TEXT,
        },
        // Fixed headroom above the curve peak (~0.399) keeps band chips clear.
        yAxis: { type: 'value', show: false, min: 0, max: 0.45 },
        series: [
            {
                type: 'line',
                name: spec.title || 'Distribuição',
                data: points,
                smooth: true,
                symbol: 'none',
                lineStyle: { width: 2.5, color: curveColor },
                areaStyle: {
                    color: verticalGradient(curveColor + '3D', curveColor + '05'),
                },
                z: 2,
            },
            decoration,
        ],
    } as EChartsOption;
};

/* ----------------------------------------------------------------------------
 *  Gauge geometry: a single value on an arc/track.
 *
 *  ECharts' native gauge: the zone bands paint the axis arc, a round-capped
 *  progress arc fills min→value in the value color, and the precomputed value
 *  string renders at the center (no pointer — the arc IS the reading).
 * ------------------------------------------------------------------------- */

const GAUGE_TRACK_COLOR = 'rgba(61,82,217,0.10)';

// Default classification-zone fills (gauge arc / bullet boxes): an ordered
// índigo tint ramp, so unstyled zones read as intensity instead of a loud
// rainbow of series colors. A user-picked band color always wins.
const ZONE_TINTS = [
    'rgba(61,82,217,0.08)',
    'rgba(61,82,217,0.16)',
    'rgba(61,82,217,0.26)',
    'rgba(61,82,217,0.38)',
    'rgba(61,82,217,0.50)',
] as const;

const zoneTintAt = (index: number): string => ZONE_TINTS[index % ZONE_TINTS.length];

// Map the zone bands onto the gauge arc as cumulative [fraction, color] stops,
// with track-colored gaps where no band covers the scale. Sorted + clipped so
// segments stay in order and never exceed the arc.
const gaugeArcStops = (spec: GaugeSpec): [number, string][] => {
    const { min, max } = spec;
    const span = max - min || 1;
    if (spec.bands.length === 0) return [[1, GAUGE_TRACK_COLOR]];
    const sorted = [...spec.bands].sort((a, b) => a.min - b.min);
    const stops: [number, string][] = [];
    let cursor = min;
    sorted.forEach((band, index) => {
        const start = band.min < min ? min : band.min;
        const end = band.max > max ? max : band.max;
        if (end <= cursor) return;
        if (start > cursor) stops.push([(start - min) / span, GAUGE_TRACK_COLOR]);
        stops.push([(end - min) / span, band.color || zoneTintAt(index)]);
        cursor = end;
    });
    if (cursor < max) stops.push([1, GAUGE_TRACK_COLOR]);
    return stops;
};

const buildGaugeOption = (spec: GaugeSpec): EChartsOption => {
    const { min, max } = spec;
    // Extraction expands the scale to fit the value, so this only guards a
    // malformed hand-built spec: a value outside [min,max] would sweep the
    // progress arc past the scale.
    const clampedValue = spec.value < min ? min : spec.value > max ? max : spec.value;
    const valueColor = spec.primaryColor || colorAt(0);

    // Both series share the sweep; every scale affordance is hidden — the arcs
    // ARE the reading.
    const arcBase = {
        type: 'gauge',
        startAngle: 225,
        endAngle: -45,
        min,
        max,
        center: ['50%', '56%'],
        pointer: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
    } as const;

    return {
        animation: false,
        series: [
            // Outer ring: the classification zones (or a bare track), always
            // visible — the progress arc must never bury them.
            {
                ...arcBase,
                radius: '86%',
                axisLine: { lineStyle: { width: 8, color: gaugeArcStops(spec) } },
                detail: { show: false },
                data: [],
            },
            // Inner ring: round-capped progress over a faint track, with the
            // precomputed value + optional context name at the center.
            {
                ...arcBase,
                radius: '72%',
                axisLine: { lineStyle: { width: 14, color: [[1, GAUGE_TRACK_COLOR]] } },
                progress: {
                    show: true,
                    width: 14,
                    roundCap: true,
                    itemStyle: { color: valueColor },
                },
                detail: {
                    formatter: `${formatPtBr(spec.value)}${spec.unit || ''}`,
                    offsetCenter: [0, '-8%'],
                    fontFamily: CHART_FONT_FAMILY,
                    fontSize: 28,
                    fontWeight: 600,
                    color: TITLE_INK,
                },
                title: {
                    show: Boolean(spec.title),
                    offsetCenter: [0, '26%'],
                    fontFamily: CHART_FONT_FAMILY,
                    fontSize: 13,
                    color: LABEL_INK,
                },
                data: [{ value: clampedValue, name: spec.title || '' }],
            },
        ],
    } as EChartsOption;
};

/* ----------------------------------------------------------------------------
 *  Bullet geometry: a single value on a horizontal level bar.
 *
 *  The stacked-transparent-bar construction: an invisible base bar spans
 *  [0→min] so the visible measure bar floats [min→value] over the scale, with
 *  the classification zones painted behind it as x-range markAreas (or a faint
 *  track when there are none). Plain data only.
 * ------------------------------------------------------------------------- */

// Short, wide canvas so the single row does not float in whitespace. Many
// bullets stack (one per trait), so keep them compact.
const BULLET_CANVAS = { width: 800, height: 180 } as const;

// Zones behind bullet/level bars: full-height vertical markAreas over the
// value scale; a faint track box stands in when the chart carries no bands.
// Zone label chips render OUTSIDE the plot, above each zone (position 'top' on
// a full-height area) — like a classification header row. Inside the plot they
// inevitably collide with a bar, and markArea draw order cannot lift them.
const zoneMarkArea = (spec: BulletSpec | LevelBarsSpec): Record<string, unknown> => ({
    silent: true,
    data:
        spec.bands.length > 0
            ? bandMarkAreaData(spec.bands, 'x', 'top', zoneTintAt)
            : [[{ xAxis: spec.min, itemStyle: { color: GAUGE_TRACK_COLOR } }, { xAxis: spec.max }]],
});

// Extra grid headroom for that chip row, only when a band actually has a label.
const zoneLabelInset = (spec: BulletSpec | LevelBarsSpec): number =>
    spec.bands.some((band) => band.label) ? 26 : 0;

const buildBulletOption = (spec: BulletSpec): EChartsOption => {
    // No clamping: extraction expands the scale to fit the value, and a
    // saturated bar would silently misreport an out-of-range score.
    const { min, max } = spec;
    const valueColor = spec.primaryColor || colorAt(0);

    return {
        animation: false,
        title: {
            text: `${formatPtBr(spec.value)}${spec.unit || ''}`,
            ...(spec.title ? { subtext: spec.title } : {}),
            left: 'center',
            top: 6,
            textStyle: {
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 28,
                fontWeight: 600,
                color: TITLE_INK,
            },
            subtextStyle: { fontFamily: CHART_FONT_FAMILY, fontSize: 13, color: LABEL_INK },
        },
        grid: {
            left: 32,
            right: 32,
            top: 84 + zoneLabelInset(spec),
            bottom: 36,
            containLabel: false,
        },
        xAxis: {
            type: 'value',
            min,
            max,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: TICK_TEXT,
            splitLine: { show: true, lineStyle: { color: AXIS_GRID_COLOR } },
        },
        yAxis: { type: 'category', data: [''], show: false },
        series: [
            {
                type: 'bar',
                stack: 'measure',
                data: [min],
                itemStyle: { color: 'transparent' },
                silent: true,
                barWidth: 24,
            },
            {
                type: 'bar',
                stack: 'measure',
                data: [spec.value - min],
                barWidth: 24,
                itemStyle: { color: valueColor, borderRadius: 4 },
                z: 2,
            },
            { type: 'bar', data: [], silent: true, z: 0, markArea: zoneMarkArea(spec) },
        ],
    } as EChartsOption;
};

/* ----------------------------------------------------------------------------
 *  Level-bars geometry: one classification level bar per row.
 *
 *  The multi-row sibling of the bullet chart — the report archetype where every
 *  table row (Vineland domain, Big Five facet, …) gets its own Baixa/Média/Alta
 *  level bar over shared classification zones. Zone label chips sit at the TOP
 *  of each zone so they never hide under a bar row. Plain data only.
 * ------------------------------------------------------------------------- */

const LEVEL_BARS_WIDTH = 800;

// Compact per-row height so a long facet list stays scannable; clamped so a
// giant table cannot produce an unbounded raster.
const levelBarsHeight = (rowCount: number): number => Math.min(140 + rowCount * 48, 600);

const buildLevelBarsOption = (spec: LevelBarsSpec): EChartsOption => {
    // No clamping: extraction expands the scale to fit the data, and a
    // saturated bar would silently misreport an out-of-range score.
    const { min, max } = spec;
    const valueColor = spec.primaryColor || colorAt(0);
    const maxRowLen = spec.rows.reduce((acc, row) => Math.max(acc, row.label.length), 0);
    // Row labels live in the left margin; estimated from character count and
    // clamped so one long facet name cannot crush the plot.
    const left = Math.max(72, Math.min(220, 24 + Math.ceil(maxRowLen * CHAR_W)));

    return {
        animation: false,
        ...(spec.title ? { title: titleComponent(spec.title) } : {}),
        grid: {
            left,
            // End-anchored value labels need room past the scale max.
            right: 48,
            top: (spec.title ? 56 : 30) + zoneLabelInset(spec),
            bottom: spec.unit ? 56 : 36,
            containLabel: false,
        },
        xAxis: {
            type: 'value',
            min,
            max,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: TICK_TEXT,
            splitLine: { show: true, lineStyle: { color: AXIS_GRID_COLOR } },
            ...(spec.unit
                ? {
                      name: spec.unit,
                      nameLocation: 'middle',
                      nameGap: 32,
                      nameTextStyle: AXIS_NAME_TEXT,
                  }
                : {}),
        },
        yAxis: {
            type: 'category',
            data: spec.rows.map((row) => row.label),
            // Table order reads top-down.
            inverse: true,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { ...TICK_TEXT, color: LABEL_INK },
        },
        series: [
            {
                type: 'bar',
                stack: 'measure',
                data: spec.rows.map(() => min),
                itemStyle: { color: 'transparent' },
                silent: true,
                barWidth: 20,
            },
            {
                type: 'bar',
                stack: 'measure',
                barWidth: 20,
                data: spec.rows.map((row) => ({
                    value: row.value - min,
                    label: { formatter: formatPtBr(row.value) },
                })),
                itemStyle: { color: valueColor, borderRadius: 4 },
                label: pointLabelFragment('right'),
                z: 2,
            },
            {
                type: 'bar',
                data: [],
                silent: true,
                z: 0,
                markArea: zoneMarkArea(spec),
            },
        ],
    } as EChartsOption;
};

/**
 * Geometry registry — one entry per ChartSpec `kind`. Co-locates a geometry's
 * option builder with its canvas aspect ratio so a new geometry is added in
 * ONE place (this map + its ChartSpec variant), instead of being scattered
 * across `buildChartOption` and `resolveCanvasSize`. The map is total over
 * `ChartSpec['kind']`, so the type-checker forces every union member to be
 * registered — a new variant cannot be half-added.
 */
interface Geometry<S extends ChartSpec = ChartSpec> {
    build(spec: S): EChartsOption;
    /** Intended raster size; omit to use the default CHART_CANVAS. */
    resolveSize?(spec: S): { width: number; height: number };
}

const GEOMETRY: {
    [K in ChartSpec['kind']]: Geometry<Extract<ChartSpec, { kind: K }>>;
} = {
    'categorical-series': {
        build: buildCategoricalSeriesOption,
        resolveSize: (spec) => {
            if (spec.chartType === 'radar') {
                const size = Math.min(CHART_CANVAS.width, CHART_CANVAS.height * 1.1);
                return { width: size, height: size };
            }
            return { width: CHART_CANVAS.width, height: CHART_CANVAS.height };
        },
    },
    'xy-scatter': {
        build: buildXyScatterOption,
    },
    distribution: {
        build: buildDistributionOption,
    },
    gauge: {
        build: buildGaugeOption,
    },
    bullet: {
        build: buildBulletOption,
        resolveSize: () => ({ width: BULLET_CANVAS.width, height: BULLET_CANVAS.height }),
    },
    'level-bars': {
        build: buildLevelBarsOption,
        resolveSize: (spec) => ({
            width: LEVEL_BARS_WIDTH,
            height: levelBarsHeight(spec.rows.length),
        }),
    },
};

/**
 * Turn a normalized `ChartSpec` into an ECharts option object.
 * Deterministic and free of function callbacks so it is JSON-serializable
 * (the parity test relies on this). One-line dispatch over GEOMETRY.
 */
export const buildChartOption = (spec: ChartSpec): EChartsOption => {
    const geometry = (GEOMETRY as Record<string, Geometry>)[spec.kind];
    // Guard: untrusted JSON can carry a `kind` outside the union at runtime.
    if (!geometry) {
        throw new Error(`buildChartOption: unsupported spec kind ${JSON.stringify(spec)}`);
    }
    return geometry.build(spec);
};

/** The intended raster dimensions for a spec (radar renders square). */
export const resolveCanvasSize = (spec: ChartSpec): { width: number; height: number } => {
    const geometry = (GEOMETRY as Record<string, Geometry>)[spec.kind];
    return (
        geometry?.resolveSize?.(spec) ?? {
            width: CHART_CANVAS.width,
            height: CHART_CANVAS.height,
        }
    );
};

/**
 * True when a spec carries no plottable data. Total over `kind`, so every
 * geometry declares its own emptiness rule in one place — the preview and the
 * .docx pipeline share this instead of each open-coding a `kind` check.
 */
export const isChartSpecEmpty = (spec: ChartSpec): boolean => {
    switch (spec.kind) {
        case 'categorical-series':
            return spec.labels.length === 0;
        case 'xy-scatter':
            return spec.points.length === 0;
        case 'distribution':
            return spec.markers.length === 0;
        case 'gauge':
            return !Number.isFinite(spec.value);
        case 'bullet':
            return !Number.isFinite(spec.value);
        case 'level-bars':
            return spec.rows.length === 0;
    }
};

/* ============================================================================
 *  TABLE EXTRACTION → SPEC (mirrored)
 *
 *  `resolveChartSpec` turns a persisted chart block plus its source table into
 *  a `ChartSpec`. It lives here — next to `buildChartOption`, byte-identical
 *  across repos — so the frontend preview and the backend .docx resolve the
 *  IDENTICAL spec from the IDENTICAL table. That is what makes WYSIWYG
 *  structural: the parity test checksums `buildChartOption(resolveChartSpec(…))`
 *  so the whole pipeline (extraction + rendering), not just the last step,
 *  cannot drift.
 *
 *  Kept pure and dependency-free (no runtime imports). Each repo supplies a
 *  thin adapter that turns its native table block into the plain
 *  `NormalizedTableView` this module consumes.
 * ========================================================================== */

/** Recognized normative metric kinds (mirrors the frontend score engine). */
export type MetricId =
    | 'raw'
    | 'mean'
    | 'sd'
    | 'z'
    | 't'
    | 'iq'
    | 'scaled10_3'
    | 'percentile'
    | 'sten'
    | 'stanine'
    | 'classification'
    | 'note';

// Column-header aliases used to recognize a metric from its label. Mirrors the
// frontend METRIC_ALIASES (src/components/preview/test-table/utils.ts).
const METRIC_ALIASES: Record<MetricId, string[]> = {
    raw: ['Resultado Bruto', 'Bruto', 'Pontos', 'Raw Score', 'Raw', 'X', 'Escore Bruto'],
    mean: ['Média', 'Mean', 'μ', 'M', 'Media', 'Média Normativa'],
    sd: ['Desvio Padrão', 'DP', 'SD', 'σ', 'S', 'Desvio Padrao', 'SD Normativo'],
    z: ['Escore Z', 'Z', 'Z-Score', 'Z Score', 'Nota Z'],
    t: ['Escore T', 'T', 'T-Score', 'T Score', 'Nota T'],
    iq: ['QI', 'IQ', 'Escore Padronizado', 'Standard Score', 'SS'],
    scaled10_3: ['Escala 10/3', 'Escore Ponderado', 'Scaled Score', 'Ponderado'],
    percentile: ['Percentil', '%', 'Percentile', 'Posto Percentílico'],
    sten: ['Sten', 'Sten Score'],
    stanine: ['Stanine', 'Stanine Score'],
    classification: ['Classificação', 'Classification', 'Classificacao', 'Interpretação', 'Faixa'],
    note: ['Obs', 'Observação', 'Note', 'Observation', 'Notas'],
};

// Header keywords that mark a descriptive (x-axis) column.
const LABEL_KEYWORDS = [
    'escala',
    'nome',
    'instrumento',
    'teste',
    'subteste',
    'fator',
    'dominio',
    'indice',
    'perfil',
    'categoria',
    'area',
    'dimensao',
    'descritor',
];

// Preference order when auto-picking the numeric (y-axis) column.
const METRIC_PRIORITY: MetricId[] = [
    'percentile',
    't',
    'z',
    'iq',
    'scaled10_3',
    'sten',
    'stanine',
    'raw',
    'mean',
    'sd',
];

const normalizeLabel = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

/** Parse a cell into a finite number, tolerating %, thousands and comma decimals. */
const parseMetricValue = (value?: string | number | null): number | undefined => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (!value) return undefined;

    const normalized = value.toString().trim().replace(/%/g, '').replace(/\s+/g, '');
    if (!normalized) return undefined;

    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');
    const numericString =
        hasComma && hasDot
            ? normalized.replace(/\./g, '').replace(',', '.')
            : hasComma
              ? normalized.replace(',', '.')
              : normalized;

    const parsed = Number.parseFloat(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const detectMetricId = (label: string): MetricId | null => {
    const normalized = normalizeLabel(label);
    for (const metricId of Object.keys(METRIC_ALIASES) as MetricId[]) {
        const matched = METRIC_ALIASES[metricId].some((alias) => {
            const aliasLabel = normalizeLabel(alias);
            return (
                normalized === aliasLabel ||
                normalized.startsWith(`${aliasLabel} `) ||
                normalized.startsWith(`${aliasLabel}(`) ||
                normalized.startsWith(`${aliasLabel}-`)
            );
        });
        if (matched) return metricId;
    }
    return null;
};

const getMetricPriority = (metricId: MetricId | null): number => {
    if (!metricId) return METRIC_PRIORITY.length + 1;
    const index = METRIC_PRIORITY.indexOf(metricId);
    return index === -1 ? METRIC_PRIORITY.length : index;
};

/** A plain, repo-agnostic view of a source table (built by each repo's adapter). */
export interface NormalizedTableColumn {
    id: string;
    /** Header text already resolved by the caller (label wins over raw cell). */
    label: string;
}
export interface NormalizedTableView {
    columns: NormalizedTableColumn[];
    /** Row-major cell values, aligned to `columns` by index. */
    rows: Array<Array<string | number | null>>;
    label?: string;
}

/**
 * Minimal structural view of a persisted table block that `normalizeTableView`
 * consumes. Both repos' table blocks are structurally assignable to this; only
 * the fields the chart pipeline needs are declared.
 */
export interface RawTableCell {
    id: string;
    value: string;
    /** Id of the merge-origin cell whose label a merged cell inherits. */
    mergedInto?: string;
}
export interface RawTableRow {
    cells: RawTableCell[];
}
export interface RawTableBlockView {
    header: RawTableRow;
    rows: RawTableRow[];
    description?: string;
}

/**
 * Build the plain `NormalizedTableView` the extractor consumes from a
 * WELL-FORMED table block (rows already aligned to the header — guaranteed by
 * both repos' table schemas). Merged header cells inherit their merge-origin
 * cell's value (`cell.mergedInto` → origin id). Pure and deterministic: no id
 * generation, so it is safe in the parity-checked mirrored core. Each repo's
 * thin adapter upgrades any legacy shapes before calling this.
 */
export const normalizeTableView = (block?: RawTableBlockView | null): NormalizedTableView => {
    if (!block) return { columns: [], rows: [], label: '' };

    const idToIndex = new Map<string, number>();
    block.header.cells.forEach((cell, index) => {
        idToIndex.set(cell.id, index);
    });

    const headerLabels = block.header.cells.map((cell) => {
        if (cell.mergedInto) {
            const originIndex = idToIndex.get(cell.mergedInto);
            if (originIndex !== undefined) {
                return String(block.header.cells[originIndex].value || '');
            }
        }
        return String(cell.value || '');
    });

    return {
        columns: block.header.cells.map((cell, index) => ({
            id: cell.id,
            label: headerLabels[index] || cell.value,
        })),
        rows: block.rows.map((row) => row.cells.map((cell) => cell.value ?? null)),
        label: block.description?.trim() || 'Tabela sem título',
    };
};

interface AnalyzedColumn {
    id: string;
    index: number;
    label: string;
    normalizedLabel: string;
    metricId: MetricId | null;
    numericValueCount: number;
    nonEmptyValueCount: number;
    isNumericCandidate: boolean;
}
export interface TableAnalysis {
    columns: AnalyzedColumn[];
    defaultXAxisColumnId: string | null;
    defaultYAxisColumnId: string | null;
    totalRows: number;
    label: string;
}

const getColumnLabel = (label: string, index: number): string =>
    label.trim() || `Coluna ${index + 1}`;

const analyzeColumns = (view: NormalizedTableView): AnalyzedColumn[] =>
    view.columns.map((column, index) => {
        let numericValueCount = 0;
        let nonEmptyValueCount = 0;

        view.rows.forEach((row) => {
            const rawValue = row[index];
            const stringValue = String(rawValue ?? '').trim();
            if (!stringValue) return;
            nonEmptyValueCount += 1;
            if (parseMetricValue(rawValue) !== undefined) numericValueCount += 1;
        });

        const metricId = detectMetricId(column.label);
        const numericRatio = nonEmptyValueCount > 0 ? numericValueCount / nonEmptyValueCount : 0;

        return {
            id: column.id,
            index,
            label: getColumnLabel(column.label, index),
            normalizedLabel: normalizeLabel(column.label),
            metricId,
            numericValueCount,
            nonEmptyValueCount,
            isNumericCandidate:
                numericValueCount > 0 &&
                metricId !== 'classification' &&
                metricId !== 'note' &&
                (numericRatio >= 0.5 || metricId !== null),
        };
    });

const pickDefaultXAxisColumnId = (columns: AnalyzedColumn[]): string | null => {
    const preferredLabelColumn = columns.find((column) =>
        LABEL_KEYWORDS.some((keyword) => column.normalizedLabel.includes(normalizeLabel(keyword))),
    );
    if (preferredLabelColumn) return preferredLabelColumn.id;

    const descriptiveColumn = columns.find((column) => !column.isNumericCandidate);
    if (descriptiveColumn) return descriptiveColumn.id;

    return columns[0]?.id ?? null;
};

const pickDefaultYAxisColumnId = (
    columns: AnalyzedColumn[],
    xAxisColumnId: string | null,
): string | null => {
    const numericCandidates = columns
        .filter((column) => column.id !== xAxisColumnId && column.isNumericCandidate)
        .sort((left, right) => {
            const metricPriority =
                getMetricPriority(left.metricId) - getMetricPriority(right.metricId);
            if (metricPriority !== 0) return metricPriority;
            const numericCountDiff = right.numericValueCount - left.numericValueCount;
            if (numericCountDiff !== 0) return numericCountDiff;
            return left.index - right.index;
        });

    return numericCandidates[0]?.id ?? null;
};

/** Column/metric analysis + default axis suggestions for a table. */
export const analyzeTable = (view: NormalizedTableView): TableAnalysis => {
    const columns = analyzeColumns(view);
    const defaultXAxisColumnId = pickDefaultXAxisColumnId(columns);
    const defaultYAxisColumnId = pickDefaultYAxisColumnId(columns, defaultXAxisColumnId);
    return {
        columns,
        defaultXAxisColumnId,
        defaultYAxisColumnId,
        totalRows: view.rows.length,
        label: view.label?.trim() || 'Tabela sem título',
    };
};

export const getColumnLabelById = (analysis: TableAnalysis, columnId?: string | null): string => {
    if (!columnId) return '';
    return analysis.columns.find((column) => column.id === columnId)?.label || '';
};

const findColumnIdByMeasurementType = (
    analysis: TableAnalysis,
    measurementType?: string,
): string | null => {
    if (!measurementType) return null;

    const normalizedMeasurementType = normalizeLabel(measurementType);
    const preferredMetricId: MetricId | null =
        normalizedMeasurementType === 'rawscore'
            ? 'raw'
            : normalizedMeasurementType === 'percentile' ||
                normalizedMeasurementType === 'percentil'
              ? 'percentile'
              : null;

    if (preferredMetricId) {
        const metricMatch = analysis.columns.find(
            (column) => column.metricId === preferredMetricId,
        );
        if (metricMatch) return metricMatch.id;
    }

    const labelMatch = analysis.columns.find(
        (column) => column.normalizedLabel === normalizedMeasurementType,
    );
    return labelMatch?.id ?? null;
};

export interface ChartSelection {
    xAxisColumnId: string | null;
    yAxisColumnId: string | null;
}

/** Validate/repair an axis selection, falling back to the smart defaults. */
export const getSafeSelection = (
    analysis: TableAnalysis,
    xAxisColumnId?: string,
    yAxisColumnId?: string,
    preferredMeasurementType?: string,
): ChartSelection => {
    const safeXAxisColumnId = analysis.columns.some((c) => c.id === xAxisColumnId)
        ? (xAxisColumnId as string)
        : analysis.defaultXAxisColumnId;

    const preferredYAxisColumnId = findColumnIdByMeasurementType(
        analysis,
        preferredMeasurementType,
    );

    const safeYAxisColumnId = analysis.columns.some((c) => c.id === yAxisColumnId)
        ? (yAxisColumnId as string)
        : preferredYAxisColumnId || analysis.defaultYAxisColumnId;

    return { xAxisColumnId: safeXAxisColumnId, yAxisColumnId: safeYAxisColumnId };
};

const getFallbackRowLabel = (rowIndex: number): string => `Linha ${rowIndex + 1}`;

/** Rows the user hid from the chart, matched by trimmed x label ("hide the
 *  Total row"). Null when nothing is excluded, so untouched charts keep a
 *  byte-identical extraction. */
const excludedLabelSet = (excluded?: string[]): Set<string> | null =>
    excluded && excluded.length ? new Set(excluded.map((label) => label.trim())) : null;

/** Extract {labels, values} for a selected x/y column pair, skipping bad rows. */
// Adjacent rows sharing a group value form one span; an empty value extends
// the running group. No groups at all when the column never names one.
const collapseGroups = (groupValues: string[]): { label: string; span: number }[] => {
    if (groupValues.length === 0 || groupValues.every((value) => !value)) return [];
    const groups: { label: string; span: number }[] = [];
    for (const value of groupValues) {
        const last = groups[groups.length - 1];
        if (last && (!value || value === last.label)) last.span += 1;
        else groups.push({ label: value, span: 1 });
    }
    return groups;
};

export const buildDatapoints = (
    view: NormalizedTableView,
    analysis: TableAnalysis,
    xAxisColumnId: string,
    yAxisColumnId: string,
    skipEmpty = true,
    groupColumnId?: string,
    excludedLabels?: string[],
    marginColumnId?: string,
): {
    labels: string[];
    values: number[];
    groups?: { label: string; span: number }[];
    /** Confidence half-widths aligned to `values` (only when a margin column is set). */
    margins?: number[];
} => {
    const xIndex = analysis.columns.find((c) => c.id === xAxisColumnId)?.index ?? -1;
    const yIndex = analysis.columns.find((c) => c.id === yAxisColumnId)?.index ?? -1;
    if (xIndex === -1 || yIndex === -1) return { labels: [], values: [] };
    const groupIndex = groupColumnId
        ? (analysis.columns.find((c) => c.id === groupColumnId)?.index ?? -1)
        : -1;
    const marginIndex = marginColumnId
        ? (analysis.columns.find((c) => c.id === marginColumnId)?.index ?? -1)
        : -1;
    const excluded = excludedLabelSet(excludedLabels);

    const labels: string[] = [];
    const values: number[] = [];
    const groupValues: string[] = [];
    const marginValues: number[] = [];
    view.rows.forEach((row, rowIndex) => {
        const rawLabel = String(row[xIndex] ?? '').trim();
        const parsedValue = parseMetricValue(row[yIndex]);
        if (parsedValue === undefined) return;
        if (skipEmpty !== false && !rawLabel) return;
        const finalLabel = rawLabel || getFallbackRowLabel(rowIndex);
        if (excluded?.has(finalLabel)) return;
        labels.push(finalLabel);
        values.push(parsedValue);
        if (groupIndex !== -1) groupValues.push(String(row[groupIndex] ?? '').trim());
        if (marginIndex !== -1) marginValues.push(parseMetricValue(row[marginIndex]) ?? 0);
    });
    const groups = collapseGroups(groupValues);
    return {
        labels,
        values,
        ...(groups.length ? { groups } : {}),
        ...(marginIndex !== -1 ? { margins: marginValues } : {}),
    };
};

/**
 * Extract a shared x-label axis plus one value array per y column. Unlike
 * `buildDatapoints` (single series, which drops rows with an unparseable y),
 * every series here must align to the SAME labels, so rows are chosen by the x
 * column only (skipping empty x labels when `skipEmpty`) and a missing/unparseable
 * y cell is filled with 0. Used only for the 2+ series path.
 */
export const buildMultiDatapoints = (
    view: NormalizedTableView,
    analysis: TableAnalysis,
    xAxisColumnId: string,
    yAxisColumnIds: string[],
    skipEmpty = true,
    groupColumnId?: string,
    excludedLabels?: string[],
): {
    labels: string[];
    series: { columnId: string; values: number[] }[];
    groups?: { label: string; span: number }[];
} => {
    const xIndex = analysis.columns.find((c) => c.id === xAxisColumnId)?.index ?? -1;
    const yColumns = yAxisColumnIds
        .map((id) => ({
            id,
            index: analysis.columns.find((c) => c.id === id)?.index ?? -1,
        }))
        .filter((c) => c.index !== -1);
    if (xIndex === -1 || yColumns.length === 0) return { labels: [], series: [] };
    const groupIndex = groupColumnId
        ? (analysis.columns.find((c) => c.id === groupColumnId)?.index ?? -1)
        : -1;
    const excluded = excludedLabelSet(excludedLabels);

    const labels: string[] = [];
    const seriesValues: number[][] = yColumns.map(() => []);
    const groupValues: string[] = [];
    view.rows.forEach((row, rowIndex) => {
        const rawLabel = String(row[xIndex] ?? '').trim();
        if (skipEmpty !== false && !rawLabel) return;
        const finalLabel = rawLabel || getFallbackRowLabel(rowIndex);
        if (excluded?.has(finalLabel)) return;
        labels.push(finalLabel);
        yColumns.forEach((col, i) => {
            seriesValues[i].push(parseMetricValue(row[col.index]) ?? 0);
        });
        if (groupIndex !== -1) groupValues.push(String(row[groupIndex] ?? '').trim());
    });

    const groups = collapseGroups(groupValues);
    return {
        labels,
        series: yColumns.map((col, i) => ({ columnId: col.id, values: seriesValues[i] })),
        ...(groups.length ? { groups } : {}),
    };
};

/** Human label for a stored `measurement_type` string. */
export const chartMetricLabel = (measurementType?: string): string => {
    if (!measurementType) return 'Valor';
    if (measurementType === 'rawScore') return 'Escore Bruto';
    if (measurementType === 'percentile') return 'Percentil';
    return measurementType;
};

/** Minimal view of a persisted chart block that `resolveChartSpec` consumes. */
export interface ChartSourceView {
    x_axis_column_id?: string;
    y_axis_column_id?: string;
    /** Ordered y columns for the multi-series path; falls back to the single id. */
    y_axis_column_ids?: string[];
    skip_empty?: boolean;
    /** User-chosen series colors keyed by y-column id; unset → brand/palette. */
    series_colors?: Record<string, string>;
    /** Columns marked as norm/reference context (população, grupo controle). */
    reference_column_ids?: string[];
    /** Single base color the distribution classification tones derive from. */
    band_color?: string;
    /** User-chosen marker shapes keyed by y-column id; unset → auto-cycle. */
    series_symbols?: Record<string, string>;
    /** Column whose adjacent equal values group the x categories into a
     *  two-level axis (CBCL scale groups, Big-Five domains). */
    group_column_id?: string;
    /** Rows hidden from the chart, matched by trimmed x label ("Total"). */
    excluded_x_labels?: string[];
    /** Manual per-category colors keyed by x label (single-series marks). */
    point_colors?: Record<string, string>;
    /** Column holding the confidence half-width (±) per row. */
    margin_column_id?: string;
}
export interface ChartBlockView {
    chart_type?: string;
    source_mode?: string;
    title?: string;
    series_label?: string;
    measurement_type?: string;
    source?: ChartSourceView;
    datapoints?: {
        x_axis: Array<string | number | null>;
        y_axis: number[];
        /** Stored multi-series cache used when no live table is available. */
        series?: { label: string; values: number[]; color?: string }[];
    };
    /** Shaded classification bands persisted on the block (cartesian only). */
    reference_bands?: { min: number; max: number; color?: string; label?: string }[];
    /** Horizontal threshold lines persisted on the block (cartesian only). */
    cutoff_lines?: { value: number; color?: string; label?: string }[];
    /** Forced y-scale bounds persisted on the block. */
    axis?: { min?: number; max?: number };
    /** Quadrant divider values persisted on the block (xy-scatter only). */
    quadrant?: { x?: number; y?: number };
    /** Semantic axis-end labels persisted on the block (xy-scatter only). */
    axis_end_labels?: { x_min?: string; x_max?: string; y_min?: string; y_max?: string };
    /** Line-chart appearance persisted on the block: smoothing + area fill
     *  (+ optional flat fill when the fade is disabled). */
    line_style?: { curve?: 'straight' | 'smooth'; fill?: boolean; fill_gradient?: boolean };
    /** Bar appearance persisted on the block; absent = default look. */
    bar_style?: {
        corner?: 'square' | 'soft' | 'round';
        thickness?: 'thin' | 'normal' | 'thick';
        gradient?: boolean;
    };
    /** Per-point score labels (undefined = on; stored only when disabled). */
    point_labels?: boolean;
    /** Single-series grouped bars: color each bar by its category group. */
    color_by_group?: boolean;
    /** Tint each mark by the reference band containing its value. */
    color_by_classification?: boolean;
    /** Emphasized category (dark ring + bold label + optional fill override). */
    highlight?: { x_label?: string; color?: string };
    /** Test–retest evolution labels between exactly two data series. */
    show_deltas?: boolean;
    /** Fixed confidence half-width fallback (used when no margin column). */
    confidence?: { fixed?: number };
}
export interface ResolveChartContext {
    /** Live source table; when present and linked, extraction wins over stored datapoints. */
    table?: NormalizedTableView | null;
    /** Series/brand color injected per repo (report theme vs docx customization). */
    primaryColor?: string;
    /** Second brand color; leads the 2nd series, and seeds darker shades beyond. */
    secondaryColor?: string;
}

const normalizeChartType = (chartType?: string): CategoricalSeriesSpec['chartType'] =>
    chartType === 'line' || chartType === 'radar' || chartType === 'scatter' ? chartType : 'bar';

type ChartExtractor = (block: ChartBlockView, ctx: ResolveChartContext) => ChartSpec;

/**
 * Optional band/cutoff/axis decorations lifted off a block, ready to spread onto
 * a spec (same conditional-spread pattern as `title`/`primaryColor`). Emits a
 * key only when the block actually carries that decoration, so undecorated
 * charts produce a byte-identical spec.
 */
const blockDecorations = (
    block: ChartBlockView,
): Pick<CategoricalSeriesSpec, 'referenceBands' | 'cutoffLines' | 'yAxis'> => ({
    ...(block.reference_bands?.length ? { referenceBands: block.reference_bands } : {}),
    ...(block.cutoff_lines?.length ? { cutoffLines: block.cutoff_lines } : {}),
    ...(block.axis && (block.axis.min !== undefined || block.axis.max !== undefined)
        ? { yAxis: block.axis }
        : {}),
});

/**
 * Optional line-chart appearance (smoothing / area fill) lifted off a block.
 * Emits a key only when the block sets it, so charts without a `line_style`
 * produce a byte-identical spec (and keep the parity checksum stable).
 */
const blockLineStyle = (
    block: ChartBlockView,
): Pick<CategoricalSeriesSpec, 'curve' | 'fillArea' | 'fillGradient'> => ({
    ...(block.line_style?.curve ? { curve: block.line_style.curve } : {}),
    ...(block.line_style?.fill ? { fillArea: true } : {}),
    ...(block.line_style?.fill_gradient === false ? { fillGradient: false } : {}),
});

/**
 * Optional bar appearance lifted off a block. Emits a key only when the block
 * overrides some part of the default look, so charts without a `bar_style`
 * produce a byte-identical spec.
 */
const blockBarStyle = (block: ChartBlockView): Pick<CategoricalSeriesSpec, 'barStyle'> =>
    block.bar_style &&
    (block.bar_style.corner || block.bar_style.thickness || block.bar_style.gradient !== undefined)
        ? { barStyle: block.bar_style }
        : {};

/**
 * Optional per-point emphasis lifted off a block: manual category colors, the
 * classification tint and the highlighted category. Emits keys only when set,
 * so untouched charts produce a byte-identical spec.
 */
/**
 * Optional evolution marks lifted off a block. Emits keys only when set, so
 * untouched charts produce a byte-identical spec.
 */
const blockAdvancedMarks = (block: ChartBlockView): Pick<CategoricalSeriesSpec, 'seriesDeltas'> => {
    return {
        ...(block.show_deltas ? { seriesDeltas: true } : {}),
    };
};

const blockPointEmphasis = (
    block: ChartBlockView,
): Pick<CategoricalSeriesSpec, 'pointColors' | 'colorByClassification' | 'highlight'> => ({
    ...(block.source?.point_colors && Object.keys(block.source.point_colors).length
        ? { pointColors: block.source.point_colors }
        : {}),
    ...(block.color_by_classification ? { colorByClassification: true } : {}),
    ...(block.highlight?.x_label
        ? {
              highlight: {
                  label: block.highlight.x_label,
                  ...(block.highlight.color ? { color: block.highlight.color } : {}),
              },
          }
        : {}),
});

/**
 * Per-point score labels are ON by default (clinical convention — the report
 * reader wants exact scores). Emits a key only when the user disabled them, so
 * every stored chart keeps a byte-identical spec.
 */
const blockPointLabels = (block: ChartBlockView): Pick<CategoricalSeriesSpec, 'pointLabels'> =>
    block.point_labels === false ? { pointLabels: false } : {};

/** Stored symbol string → validated spec symbol (unknown values are dropped). */
const sanitizeSymbol = (value?: string): SeriesSymbol | undefined =>
    value && value in SYMBOL_MAP ? (value as SeriesSymbol) : undefined;

/**
 * Color for a single-value chart (gauge/bullet/xy/distribution): the user's
 * stored color for the value column wins, else the report brand `primaryColor`.
 */
const blockValueColor = (block: ChartBlockView, ctx: ResolveChartContext): string | undefined => {
    const yId = block.source?.y_axis_column_id;
    return (yId && block.source?.series_colors?.[yId]) || ctx.primaryColor;
};

/**
 * Multi-series (2+ y columns) extractor: shares one x-label axis across all
 * series. Prefers live extraction from the linked table and falls back to the
 * stored `datapoints.series` cache. Returns null when neither is available, so
 * the caller can degrade to the single-series path.
 */
const extractMultiSeries = (
    block: ChartBlockView,
    ctx: ResolveChartContext,
    requestedYIds: string[],
    base: {
        chartType: CategoricalSeriesSpec['chartType'];
        primaryColor?: string;
        secondaryColor?: string;
        title?: string;
    },
): ChartSpec | null => {
    const { chartType, primaryColor, secondaryColor, title } = base;
    let labels: string[] = [];
    let series: ChartSeries[] = [];
    let groups: { label: string; span: number }[] | undefined;

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' && !!table && !!block.source?.x_axis_column_id;

    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const safeXAxisColumnId = getSafeSelection(
            analysis,
            block.source?.x_axis_column_id,
            undefined,
            block.measurement_type,
        ).xAxisColumnId;
        const validYIds = requestedYIds.filter((id) => analysis.columns.some((c) => c.id === id));
        if (safeXAxisColumnId && validYIds.length > 0) {
            const points = buildMultiDatapoints(
                table,
                analysis,
                safeXAxisColumnId,
                validYIds,
                block.source?.skip_empty,
                block.source?.group_column_id,
                block.source?.excluded_x_labels,
            );
            labels = points.labels;
            groups = points.groups;
            series = points.series.map((s) => ({
                label: getColumnLabelById(analysis, s.columnId) || 'Valor',
                values: s.values,
                ...(block.source?.series_colors?.[s.columnId]
                    ? { color: block.source.series_colors[s.columnId] }
                    : {}),
                ...(block.source?.reference_column_ids?.includes(s.columnId)
                    ? { role: 'reference' as const }
                    : {}),
                ...(sanitizeSymbol(block.source?.series_symbols?.[s.columnId])
                    ? { symbol: sanitizeSymbol(block.source?.series_symbols?.[s.columnId]) }
                    : {}),
            }));
        }
    }

    if (series.length === 0 && block.datapoints?.series?.length) {
        labels = (block.datapoints.x_axis ?? []).map((x) => String(x ?? ''));
        series = block.datapoints.series.map((s) => ({
            label: s.label,
            values: labels.map((_, i) => s.values[i] ?? 0),
            ...(s.color ? { color: s.color } : {}),
        }));
        const excluded = excludedLabelSet(block.source?.excluded_x_labels);
        if (excluded) {
            const keep = labels.map((label, i) => [label, i] as const);
            const kept = keep.filter(([label]) => !excluded.has(label.trim()));
            labels = kept.map(([label]) => label);
            series = series.map((s) => ({
                ...s,
                values: kept.map(([, i]) => s.values[i]),
            }));
        }
    }

    if (series.length === 0) return null;

    // Brand colors lead the first two series unless already colored or a
    // norm/reference context; the palette (or darker secondary shades) colors
    // the rest, resolved at render time in seriesColor.
    if (primaryColor) {
        series = series.map((s, i) =>
            i === 0 && !s.color && s.role !== 'reference' ? { ...s, color: primaryColor } : s,
        );
    }
    if (secondaryColor) {
        series = series.map((s, i) =>
            i === 1 && !s.color && s.role !== 'reference' ? { ...s, color: secondaryColor } : s,
        );
    }

    return {
        kind: 'categorical-series',
        chartType,
        labels,
        series,
        ...(primaryColor ? { primaryColor } : {}),
        ...(secondaryColor ? { secondaryColor } : {}),
        ...(title ? { title } : {}),
        ...(groups?.length ? { categoryGroups: groups } : {}),
        ...(block.color_by_group ? { colorByGroup: true } : {}),
        ...blockDecorations(block),
        ...blockLineStyle(block),
        ...blockBarStyle(block),
        ...blockPointEmphasis(block),
        ...blockAdvancedMarks(block),
        ...blockPointLabels(block),
    };
};

/**
 * Extractor for the `categorical-series` geometry: prefers live extraction from
 * the linked table (self-healing) and falls back to the block's stored
 * datapoints.
 */
const extractCategoricalSeries: ChartExtractor = (block, ctx) => {
    const chartType = normalizeChartType(block.chart_type);
    const primaryColor = ctx.primaryColor;
    const secondaryColor = ctx.secondaryColor;
    const title = block.title?.trim() || undefined;
    const explicitSeriesLabel = block.series_label?.trim() || '';

    // Multi-series path: 2+ y columns requested. The single-series code below is
    // left byte-identical, so existing charts render exactly as before.
    const requestedYIds = block.source?.y_axis_column_ids ?? [];
    if (requestedYIds.length > 1) {
        const multi = extractMultiSeries(block, ctx, requestedYIds, {
            chartType,
            primaryColor,
            secondaryColor,
            title,
        });
        if (multi) return multi;
    }

    let labels: string[] = [];
    let values: number[] = [];
    let groups: { label: string; span: number }[] | undefined;
    let margins: number[] | undefined;
    let resolvedMeasurementLabel = chartMetricLabel(block.measurement_type);

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' &&
        !!table &&
        !!block.source?.x_axis_column_id &&
        !!block.source?.y_axis_column_id;

    let usedTable = false;
    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const selection = getSafeSelection(
            analysis,
            block.source?.x_axis_column_id,
            block.source?.y_axis_column_id,
            block.measurement_type,
        );
        if (selection.xAxisColumnId && selection.yAxisColumnId) {
            const points = buildDatapoints(
                table,
                analysis,
                selection.xAxisColumnId,
                selection.yAxisColumnId,
                block.source?.skip_empty,
                block.source?.group_column_id,
                block.source?.excluded_x_labels,
                block.source?.margin_column_id,
            );
            labels = points.labels;
            values = points.values;
            groups = points.groups;
            margins = points.margins;
            resolvedMeasurementLabel =
                getColumnLabelById(analysis, selection.yAxisColumnId) ||
                chartMetricLabel(block.measurement_type);
            usedTable = true;
        }
    }

    if (!usedTable && block.datapoints?.x_axis) {
        const storedY = block.datapoints.y_axis || [];
        labels = block.datapoints.x_axis.map((x) => String(x ?? ''));
        values = block.datapoints.x_axis.map((_, i) => storedY[i] ?? 0);
        const excluded = excludedLabelSet(block.source?.excluded_x_labels);
        if (excluded) {
            const kept = labels
                .map((label, i) => [label, i] as const)
                .filter(([label]) => !excluded.has(label.trim()));
            labels = kept.map(([label]) => label);
            values = kept.map(([, i]) => values[i]);
        }
    }

    // Fixed confidence half-width when no margin column produced values
    // (manual charts, or the user typed a constant SEM/CI).
    if (!margins?.length) {
        const fixed = block.confidence?.fixed;
        if (fixed !== undefined && fixed > 0 && values.length) {
            margins = values.map(() => fixed);
        }
    }

    const seriesLabel = explicitSeriesLabel || resolvedMeasurementLabel || 'Valor';

    // The user's stored color for this y column wins; else the brand color.
    const singleYId = block.source?.y_axis_column_id;
    const storedColor = (singleYId && block.source?.series_colors?.[singleYId]) || undefined;
    const seriesColorValue = storedColor || primaryColor;

    const singleSymbol = sanitizeSymbol(
        singleYId ? block.source?.series_symbols?.[singleYId] : undefined,
    );

    return {
        kind: 'categorical-series',
        chartType,
        labels,
        series: [
            {
                label: seriesLabel,
                values,
                ...(seriesColorValue ? { color: seriesColorValue } : {}),
                ...(singleSymbol ? { symbol: singleSymbol } : {}),
            },
        ],
        ...(primaryColor ? { primaryColor } : {}),
        ...(title ? { title } : {}),
        ...(groups?.length ? { categoryGroups: groups } : {}),
        ...(block.color_by_group ? { colorByGroup: true } : {}),
        ...(margins?.length ? { margins } : {}),
        ...blockDecorations(block),
        ...blockLineStyle(block),
        ...blockBarStyle(block),
        ...blockPointEmphasis(block),
        ...blockAdvancedMarks(block),
        ...blockPointLabels(block),
    };
};

/**
 * Extractor for the `xy-scatter` geometry: pairs two numeric columns into {x,y}
 * points. Prefers the live linked table (skipping rows where either value is
 * non-numeric) and falls back to the block's stored datapoints.
 */
const extractXyScatter: ChartExtractor = (block, ctx) => {
    const primaryColor = blockValueColor(block, ctx);
    const title = block.title?.trim() || undefined;
    const points: { x: number; y: number; label?: string }[] = [];
    let xLabel = '';
    let yLabel = '';

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' &&
        !!table &&
        !!block.source?.x_axis_column_id &&
        !!block.source?.y_axis_column_id;

    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const xId = block.source?.x_axis_column_id;
        const yId = block.source?.y_axis_column_id;
        const xIndex = analysis.columns.find((c) => c.id === xId)?.index ?? -1;
        const yIndex = analysis.columns.find((c) => c.id === yId)?.index ?? -1;
        if (xIndex !== -1 && yIndex !== -1) {
            xLabel = getColumnLabelById(analysis, xId);
            yLabel = getColumnLabelById(analysis, yId);
            table.rows.forEach((row) => {
                const x = parseMetricValue(row[xIndex]);
                const y = parseMetricValue(row[yIndex]);
                if (x === undefined || y === undefined) return;
                points.push({ x, y });
            });
        }
    }

    if (points.length === 0 && block.datapoints?.x_axis) {
        const storedY = block.datapoints.y_axis || [];
        block.datapoints.x_axis.forEach((rawX, i) => {
            const x = parseMetricValue(rawX);
            const y = parseMetricValue(storedY[i]);
            if (x === undefined || y === undefined) return;
            points.push({ x, y });
        });
    }

    return {
        kind: 'xy-scatter',
        points,
        ...(xLabel ? { xLabel } : {}),
        ...(yLabel ? { yLabel } : {}),
        ...(primaryColor ? { primaryColor } : {}),
        ...(title ? { title } : {}),
        ...(block.quadrant?.x !== undefined ? { quadrantX: block.quadrant.x } : {}),
        ...(block.quadrant?.y !== undefined ? { quadrantY: block.quadrant.y } : {}),
        ...(block.axis_end_labels &&
        (block.axis_end_labels.x_min ||
            block.axis_end_labels.x_max ||
            block.axis_end_labels.y_min ||
            block.axis_end_labels.y_max)
            ? {
                  endLabels: {
                      ...(block.axis_end_labels.x_min ? { xMin: block.axis_end_labels.x_min } : {}),
                      ...(block.axis_end_labels.x_max ? { xMax: block.axis_end_labels.x_max } : {}),
                      ...(block.axis_end_labels.y_min ? { yMin: block.axis_end_labels.y_min } : {}),
                      ...(block.axis_end_labels.y_max ? { yMax: block.axis_end_labels.y_max } : {}),
                  },
              }
            : {}),
    };
};

/**
 * Convert a normative metric value to a z-score for the distribution geometry.
 * Linear scales invert directly; percentile goes through the probit; anything
 * without a standardized scale (raw/mean/unknown) is used as-is.
 */
const metricValueToZ = (value: number, metricId: MetricId | null): number => {
    switch (metricId) {
        case 't':
            return (value - 50) / 10;
        case 'iq':
            return (value - 100) / 15;
        case 'scaled10_3':
            return (value - 10) / 3;
        case 'sten':
            return (value - 5.5) / 2;
        case 'stanine':
            return (value - 5) / 2;
        case 'percentile':
            return inverseNormalCdf(value / 100);
        default:
            return value;
    }
};

/**
 * Extractor for the `distribution` geometry: turns a descriptive x column + a
 * numeric score column into markers on the standard normal curve. The score
 * column's detected metric drives the z conversion (live table), or the stored
 * datapoints + measurement_type do offline. Bands + domain are fixed clinical
 * defaults.
 */
const extractDistribution: ChartExtractor = (block, ctx) => {
    const primaryColor = blockValueColor(block, ctx);
    const title = block.title?.trim() || undefined;
    const domain = DISTRIBUTION_DOMAIN;
    const markers: { z: number; label?: string }[] = [];

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' &&
        !!table &&
        !!block.source?.x_axis_column_id &&
        !!block.source?.y_axis_column_id;

    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const selection = getSafeSelection(
            analysis,
            block.source?.x_axis_column_id,
            block.source?.y_axis_column_id,
            block.measurement_type,
        );
        const xIndex = analysis.columns.find((c) => c.id === selection.xAxisColumnId)?.index ?? -1;
        const yColumn = analysis.columns.find((c) => c.id === selection.yAxisColumnId);
        const yIndex = yColumn?.index ?? -1;
        if (xIndex !== -1 && yIndex !== -1) {
            const excluded = excludedLabelSet(block.source?.excluded_x_labels);
            table.rows.forEach((row) => {
                const value = parseMetricValue(row[yIndex]);
                if (value === undefined) return;
                const label = String(row[xIndex] ?? '').trim();
                if (excluded?.has(label)) return;
                markers.push({
                    z: clampToDomain(
                        roundZ(metricValueToZ(value, yColumn?.metricId ?? null)),
                        domain,
                    ),
                    ...(label ? { label } : {}),
                });
            });
        }
    }

    if (markers.length === 0 && block.datapoints?.x_axis) {
        const metricId = detectMetricId(block.measurement_type ?? '');
        const storedY = block.datapoints.y_axis || [];
        const excluded = excludedLabelSet(block.source?.excluded_x_labels);
        block.datapoints.x_axis.forEach((rawX, i) => {
            const value = parseMetricValue(storedY[i]);
            if (value === undefined) return;
            const label = String(rawX ?? '').trim();
            if (excluded?.has(label)) return;
            markers.push({
                z: clampToDomain(roundZ(metricValueToZ(value, metricId)), domain),
                ...(label ? { label } : {}),
            });
        });
    }

    return {
        kind: 'distribution',
        markers,
        bands: buildDistributionBands(block.source?.band_color || DEFAULT_DISTRIBUTION_BAND_COLOR),
        domain,
        ...(primaryColor ? { primaryColor } : {}),
        ...(title ? { title } : {}),
    };
};

/**
 * Data-driven scale for the value geometries (gauge/bullet/level bars): the
 * block's stored `axis` wins but expands to fit the data; without a stored
 * axis the scale is derived from the data itself (nice-rounded, anchored at
 * zero for positive scores). The legacy 0..100 only remains as the fallback
 * when there is no finite data at all.
 */
const resolveValueScale = (
    axis: ChartBlockView['axis'],
    values: number[],
): { min: number; max: number } => {
    const finite = values.filter((v) => Number.isFinite(v));
    const dataMin = finite.length ? Math.min(...finite) : undefined;
    const dataMax = finite.length ? Math.max(...finite) : undefined;
    let min = axis?.min ?? (dataMin !== undefined ? Math.min(0, niceFloor(dataMin)) : 0);
    let max = axis?.max ?? (dataMax !== undefined ? niceCeil(dataMax) : 100);
    if (dataMin !== undefined) min = expandedMin(min, dataMin);
    if (dataMax !== undefined) max = expandedMax(max, dataMax);
    // Degenerate data (e.g. a lone 0) collapses the scale; reopen the legacy track.
    if (!(max > min)) max = min + 100;
    return { min, max };
};

/**
 * Shared resolution for the single-value geometries (`gauge`, `bullet`). Takes
 * the FIRST scored row of the numeric column as the value, the block's `axis`
 * as the min/max scale (data-adaptive via `resolveValueScale`), and
 * `reference_bands` (WS-C2) as the colored zones. The row's x label titles the
 * chart when the block has no explicit title. Prefers the live table, falling
 * back to stored datapoints.
 */
const resolveSingleValue = (
    block: ChartBlockView,
    ctx: ResolveChartContext,
): {
    value: number;
    min: number;
    max: number;
    bands: NonNullable<ChartBlockView['reference_bands']>;
    primaryColor?: string;
    title?: string;
} => {
    const primaryColor = blockValueColor(block, ctx);
    const bands = block.reference_bands ?? [];
    let value = Number.NaN;
    let label = '';

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' &&
        !!table &&
        !!block.source?.x_axis_column_id &&
        !!block.source?.y_axis_column_id;

    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const selection = getSafeSelection(
            analysis,
            block.source?.x_axis_column_id,
            block.source?.y_axis_column_id,
            block.measurement_type,
        );
        const xIndex = analysis.columns.find((c) => c.id === selection.xAxisColumnId)?.index ?? -1;
        const yIndex = analysis.columns.find((c) => c.id === selection.yAxisColumnId)?.index ?? -1;
        if (yIndex !== -1) {
            for (const row of table.rows) {
                const parsed = parseMetricValue(row[yIndex]);
                if (parsed === undefined) continue;
                value = parsed;
                label = xIndex !== -1 ? String(row[xIndex] ?? '').trim() : '';
                break;
            }
        }
    }

    if (!Number.isFinite(value) && block.datapoints?.y_axis?.length) {
        const storedY = block.datapoints.y_axis;
        const storedX = block.datapoints.x_axis ?? [];
        for (let i = 0; i < storedY.length; i += 1) {
            const parsed = parseMetricValue(storedY[i]);
            if (parsed === undefined) continue;
            value = parsed;
            label = String(storedX[i] ?? '').trim();
            break;
        }
    }

    const title = block.title?.trim() || label || undefined;

    return {
        value,
        ...resolveValueScale(block.axis, [value]),
        bands,
        ...(primaryColor ? { primaryColor } : {}),
        ...(title ? { title } : {}),
    };
};

/**
 * Extractor for the `gauge` geometry: the resolved single value on a radial arc.
 */
const extractGauge: ChartExtractor = (block, ctx) => ({
    kind: 'gauge',
    ...resolveSingleValue(block, ctx),
});

/**
 * Extractor for the `bullet` geometry: the same single value on a horizontal
 * level bar (bullet chart). Zones/scale come from the block's bands/axis.
 */
const extractBullet: ChartExtractor = (block, ctx) => ({
    kind: 'bullet',
    ...resolveSingleValue(block, ctx),
});

/**
 * Extractor for the `level-bars` geometry: EVERY scored row becomes one level
 * bar (labels from the x column, values from the y column) over the block's
 * `reference_bands` zones and `axis` scale (data-adaptive via
 * `resolveValueScale`, like the other value geometries). Prefers the live
 * table, falling back to stored datapoints.
 */
const extractLevelBars: ChartExtractor = (block, ctx) => {
    const primaryColor = blockValueColor(block, ctx);
    const bands = block.reference_bands ?? [];
    let labels: string[] = [];
    let values: number[] = [];

    const table = ctx.table;
    const canExtract =
        block.source_mode !== 'manual' &&
        !!table &&
        !!block.source?.x_axis_column_id &&
        !!block.source?.y_axis_column_id;

    if (canExtract && table) {
        const analysis = analyzeTable(table);
        const selection = getSafeSelection(
            analysis,
            block.source?.x_axis_column_id,
            block.source?.y_axis_column_id,
            block.measurement_type,
        );
        if (selection.xAxisColumnId && selection.yAxisColumnId) {
            const points = buildDatapoints(
                table,
                analysis,
                selection.xAxisColumnId,
                selection.yAxisColumnId,
                block.source?.skip_empty,
                undefined,
                block.source?.excluded_x_labels,
            );
            labels = points.labels;
            values = points.values;
        }
    }

    if (labels.length === 0 && block.datapoints?.x_axis) {
        const storedY = block.datapoints.y_axis || [];
        block.datapoints.x_axis.forEach((rawX, i) => {
            const parsed = parseMetricValue(storedY[i]);
            if (parsed === undefined) return;
            labels.push(String(rawX ?? '').trim());
            values.push(parsed);
        });
    }

    const title = block.title?.trim() || undefined;

    return {
        kind: 'level-bars',
        rows: labels.map((label, i) => ({ label, value: values[i] })),
        ...resolveValueScale(block.axis, values),
        bands,
        ...(primaryColor ? { primaryColor } : {}),
        ...(title ? { title } : {}),
    };
};

/**
 * Extraction registry — one entry per `chart_type`. Maps a stored chart type to
 * the extractor that turns its block (+ table) into a ChartSpec. Adding a chart
 * type = one entry here (plus its geometry in GEOMETRY). Unknown/legacy types
 * fall back to the categorical extractor, matching the historical default.
 */
const EXTRACT: Record<string, ChartExtractor> = {
    bar: extractCategoricalSeries,
    line: extractCategoricalSeries,
    radar: extractCategoricalSeries,
    scatter: extractCategoricalSeries,
    xy_scatter: extractXyScatter,
    distribution: extractDistribution,
    gauge: extractGauge,
    bullet: extractBullet,
    level_bars: extractLevelBars,
};

/**
 * Resolve a persisted chart block into a `ChartSpec` — the single place that
 * knows how a stored chart becomes a renderable spec, shared by the preview and
 * the .docx. One-line dispatch over EXTRACT.
 */
export const resolveChartSpec = (block: ChartBlockView, ctx: ResolveChartContext = {}): ChartSpec =>
    (EXTRACT[block.chart_type ?? ''] ?? extractCategoricalSeries)(block, ctx);
